// ContactsDirectoryPage.jsx — Vendors, Contractors & Suppliers Directory
// Location: src/pages/projects/ContactsDirectoryPage.jsx
//
// Dense review table (mirrors AdminTeamOverviewPage patterns):
//   - Company rows with inline-editable fields
//   - Expandable company-name drawer showing contact people
//   - Sort/filter toolbar
//   - Add / delete companies and contact people

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  ChevronDown, ChevronRight, ChevronLeft, Plus, Trash2, X, Search,
  Star, ExternalLink, Linkedin, Instagram, Upload, ArrowRight,
  GripVertical, Lock, Unlock, FolderOpen, Check, Loader2, Wand2,
  ArrowUpDown, Filter, GitMerge, Users,
} from 'lucide-react';
import { BrioDatePicker, BrioModal } from '../../components/BrioComponents';
import { useSpecialismOptions } from '../../hooks/useSpecialismOptions';
import { getCountryFromPhone, getCityFromLandline } from '../../utils/phoneCodeHelpers';
import { supabase } from '../../supabaseClient';

// ══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════

const BRIO_GOLD = '#9f902a';
const BRIO_GREEN = '#689581';
const BRIO_RED = '#934948';
const BRIO_BORDER = '#e5e5e5';
const BRIO_BG = '#fafaf8';
const BRIO_GREY = '#999';
// Access-level tints, derived from the BRIO presets:
//   --brio-gold at 49% strength over white   (#9f902a -> #bcb16a -> #d0c897)
//   --brio-grey-light, deviation from white raised 80% (#f5f5f5 -> #f3f3f3 -> #ededed)
const ACCESS_TINT_FULL = '#d0c897';
const ACCESS_TINT_READ = '#ededed';
// Locked admin rows take their own lighter cast of the full-access tint (30%
// lighter than the 0.75-opacity rendering it replaces) rather than the live
// control dimmed by opacity: opacity fades the label along with the fill, and at
// this lightness that left "Full access" at 2.3:1. Spelling the muted label and
// border out keeps it at 4.4:1 and still visibly non-editable.
const ACCESS_TINT_FULL_LOCKED = '#e6e2c8';
const FONT = 'Raleway, sans-serif';

const CATEGORY_OPTIONS = [
  'Vendor / Supplier',
  'Contractor',
  'Consultants',
  'Media',
];

const CATEGORY_BADGE_COLORS = {
  'Vendor / Supplier': { bg: 'rgba(104,149,129,0.12)', color: '#689581' },   // brio-teal
  'Contractor':        { bg: 'rgba(219,107,93,0.1)',    color: '#DB6B5D' },   // brio-orange
  'Consultants':       { bg: 'rgba(132,164,140,0.15)',  color: '#84A48C' },   // brio-green
  'Media':             { bg: 'rgba(236,210,136,0.15)',  color: '#a5935f' },   // brio-yellow
};

const GENERIC_EMAIL_DOMAINS = /^(gmail|yahoo|hotmail|outlook|live|icloud|aol|protonmail|proton|zoho|yandex|mail|gmx|fastmail|hey)\./i;

/** Tidy a website string: strip www. prefix, trailing slashes. Protocol is preserved. */
// Normalise a LinkedIn value to a full https URL.
// Handles: full URL, protocol-less domain, bare "in/slug", bare slug.
function cleanLinkedinUrl(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  // Already a full URL
  if (/^https?:\/\//i.test(s)) return s;
  // Contains the domain (e.g. "linkedin.com/in/slug" or "www.linkedin.com/in/slug")
  if (/linkedin\.com/i.test(s)) return 'https://' + s.replace(/^\/+/, '');
  // Starts with "in/" or "pub/" — LinkedIn profile path
  if (/^(in|pub|company)\//.test(s)) return 'https://www.linkedin.com/' + s;
  // Bare username / slug — assume personal profile
  return 'https://www.linkedin.com/in/' + s;
}

function cleanWebsite(url) {
  if (!url || typeof url !== 'string') return '';
  let s = url.trim().replace(/\/+$/, '');
  // Strip www. after the protocol (if any)
  s = s.replace(/^(https?:\/\/)www\./i, '$1').replace(/^www\./i, '');
  return s;
}

/** Extract a website domain from an email address (skip generic providers) */
function websiteFromEmail(email) {
  if (!email || !email.includes('@')) return '';
  const domain = email.split('@')[1]?.trim().toLowerCase();
  if (!domain || GENERIC_EMAIL_DOMAINS.test(domain)) return '';
  return domain;
}

// localStorage keys for column preferences
const CD_COL_ORDER_KEY = 'cd-col-order';
const CD_FROZEN_COLS_KEY = 'cd-frozen-cols';
const CD_COLLAPSED_COLS_KEY = 'cd-collapsed-cols';
const CD_FILTER_RULES_KEY = 'cd-filter-rules';
const CD_CATEGORY_TAB_KEY = 'cd-category-tab';
const COLLAPSED_W = 18;

const RO_STYLE = { background: '#f8f8f6', color: '#555' };

// Read-only context — when true, every inline editor on the page renders as
// plain, non-interactive text. Provided once at the page root so the dozens of
// EditableCell instances don't each need a prop threaded through. Read-only
// viewers are users granted "Read only" access via the Give Access control.
const ContactsReadOnlyContext = React.createContext(false);

// Access levels offered by the Give Access modal's per-user dropdown.
const ACCESS_LEVEL_OPTIONS = [
  { value: 'none', label: 'No access' },
  { value: 'read', label: 'Read only' },
  { value: 'full', label: 'Full access' },
];

// Fill for the Give Access level picker, so the granted level reads at a glance
// down the list. Tints derive from the BRIO presets: gold (--brio-gold) for full
// access, soft grey (--brio-grey-light) for read only, plain white for no access.
// The full-access fill keeps the undiluted gold as its border, so the level still
// reads as gold at these light tints. Its label is dark rather than white: white on
// #d0c897 is 1.6:1, far under AA, where #333 is 7.5:1.
const ACCESS_LEVEL_STYLES = {
  none: { background: '#fff',              color: '#333', border: BRIO_BORDER },
  read: { background: ACCESS_TINT_READ,    color: '#555', border: BRIO_BORDER },
  full: { background: ACCESS_TINT_FULL,    color: '#333', border: BRIO_GOLD },
};
const ACCESS_LEVEL_STYLE_LOCKED = { background: ACCESS_TINT_FULL_LOCKED, color: '#666', border: '#b7ac5f' };

// ══════════════════════════════════════════════════════════════════════════
// FUZZY MATCHING UTILITIES (duplicate detection for import & add company)
// ══════════════════════════════════════════════════════════════════════════

/** Levenshtein edit distance between two strings (2-row DP). */
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  if (a === b) return 0;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
    }
    prev = curr;
  }
  return prev[n];
}

/** Regex patterns for common company legal suffixes (longer patterns first). */
const COMPANY_SUFFIX_PATTERNS = [
  /\b(private\s+limited|pvt\.?\s*ltd\.?|pvt\s+limited|pte\.?\s*ltd\.?)\b/gi,
  /\b(limited\s+liability\s+company|llc)\b/gi,
  /\b(incorporated|inc\.?)\b/gi,
  /\b(limited|ltd\.?)\b/gi,
  /\b(corporation|corp\.?)\b/gi,
  /\b(company|co\.?)\b/gi,
  /\b(enterprises?|ent\.?)\b/gi,
  /\b(associates?|assoc\.?)\b/gi,
  /\b(group|intl\.?|international)\b/gi,
  /\b(gmbh|s\.?a\.?|plc|lp|llp)\b/gi,
];

/**
 * Normalize a company name for matching: strip legal suffixes, leading "the",
 * "m/s", "&"/"and", punctuation, and collapse whitespace.
 */
function normalizeCompanyName(name) {
  if (!name) return '';
  let s = name.toLowerCase().trim();
  for (const pat of COMPANY_SUFFIX_PATTERNS) s = s.replace(pat, '');
  s = s.replace(/\bm\/s\.?\s*/g, '');
  s = s.replace(/^the\s+/, '');
  s = s.replace(/\band\b/g, '').replace(/&/g, '');
  s = s.replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
  return s;
}

/**
 * Score how well a candidate company name matches an existing vendor name.
 * Returns { score, level: 'exact'|'close'|'fuzzy'|'none', distance }.
 */
function scoreCompanyMatch(candidateRaw, existingRaw) {
  const cLower = (candidateRaw || '').toLowerCase().trim();
  const eLower = (existingRaw || '').toLowerCase().trim();
  if (!cLower || !eLower) return { score: 0, level: 'none', distance: Infinity };

  // Exact case-insensitive match
  if (cLower === eLower) return { score: 100, level: 'exact', distance: 0 };

  const cNorm = normalizeCompanyName(candidateRaw);
  const eNorm = normalizeCompanyName(existingRaw);
  if (!cNorm || !eNorm) return { score: 0, level: 'none', distance: Infinity };

  // Exact match after suffix normalization
  if (cNorm === eNorm) return { score: 90, level: 'close', distance: 0 };

  // Levenshtein on normalized forms
  const dist = levenshtein(cNorm, eNorm);
  const maxLen = Math.max(cNorm.length, eNorm.length);

  if (dist === 1 && maxLen >= 4) return { score: 85, level: 'close', distance: 1 };
  if (dist === 2 && maxLen >= 6) return { score: 75, level: 'fuzzy', distance: 2 };

  // Substring containment
  if (cNorm.length >= 4 && eNorm.length >= 4) {
    if (eNorm.includes(cNorm) || cNorm.includes(eNorm)) {
      return { score: 65, level: 'fuzzy', distance: dist };
    }
  }

  return { score: 0, level: 'none', distance: dist };
}

/**
 * Find all vendors matching a company name, ranked by score descending.
 * Returns array of { vendor, score, level, distance }.
 */
function findMatchingVendors(companyName, vendors, { limit = 8, minScore = 60 } = {}) {
  if (!companyName?.trim() || !vendors?.length) return [];
  const results = [];
  for (const v of vendors) {
    const m = scoreCompanyMatch(companyName, v.companyName);
    if (m.score >= minScore) results.push({ vendor: v, ...m });
  }
  results.sort((a, b) => b.score - a.score);
  return limit ? results.slice(0, limit) : results;
}

// ══════════════════════════════════════════════════════════════════════════
// MERGE DUPLICATES — detection, field config, modal
// ══════════════════════════════════════════════════════════════════════════

function getPairKey(group) {
  return `${group.type}:${Math.min(group.a.id, group.b.id)}-${Math.max(group.a.id, group.b.id)}`;
}

// Fuzzy-match a comma/semicolon-separated CSV input against a list of option
// strings. Each token is matched independently and the best-scoring option is
// chosen (min score 40/100). Scoring tiers:
//   100 — exact (space+punct stripped, case-insensitive)
//    85 — compact substring (one contains the other after stripping spaces)
//    75 — normalised substring
//    60 — word-prefix overlap (each input word starts/ends an option word)
//    55 — Levenshtein distance ≤ 25% of the longer string (min 2 edits free)
function fuzzyMatchMultiple(input, options) {
  if (!input || !options || !options.length) return '';

  // norm: lowercase, non-alphanum → space, collapse spaces
  const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
  // compact: strip ALL non-alphanum (space-insensitive comparison)
  const compact = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  const levenshtein = (a, b) => {
    if (a === b) return 0;
    const m = a.length, n = b.length;
    if (!m) return n;
    if (!n) return m;
    const dp = Array.from({ length: m + 1 }, (_, i) => [i]);
    for (let j = 1; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++)
      for (let j = 1; j <= n; j++)
        dp[i][j] = a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    return dp[m][n];
  };

  const tokens = input.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
  const matched = [];

  for (const token of tokens) {
    const inp = norm(token);
    const inpC = compact(token);
    let best = null, bestScore = 0;

    for (const opt of options) {
      const optN = norm(opt);
      const optC = compact(opt);
      let score = 0;

      if (optC === inpC || optN === inp) {
        // Exact (space/case insensitive)
        score = 100;
      } else if (optC.includes(inpC) || inpC.includes(optC)) {
        // Space-stripped substring — scale by coverage
        score = Math.round(85 * Math.min(optC.length, inpC.length) / Math.max(optC.length, inpC.length));
      } else if (optN.includes(inp) || inp.includes(optN)) {
        // Normalised substring
        score = Math.round(75 * Math.min(optN.length, inp.length) / Math.max(optN.length, inp.length));
      }

      // Word-prefix overlap — runs independently and can boost a low substring score
      // (e.g. "Vendor" is a substring of "vendorsupplier" at 36pts but word-prefix gives 60pts)
      {
        const iw = inp.split(/\s+/).filter(w => w.length > 1);
        const ow = optN.split(/\s+/).filter(w => w.length > 1);
        const hits = iw.filter(w => ow.some(o => o.startsWith(w) || w.startsWith(o))).length;
        if (hits > 0) score = Math.max(score, Math.round(60 * hits / iw.length));
      }

      // Levenshtein fallback (compact strings, min 4 chars)
      if (score < 40 && inpC.length >= 4) {
        const dist = levenshtein(inpC, optC);
        const maxLen = Math.max(inpC.length, optC.length);
        if (dist <= Math.max(2, Math.floor(maxLen * 0.25)))
          score = Math.max(score, Math.round(55 * (1 - dist / maxLen)));
      }

      if (score > bestScore) { bestScore = score; best = opt; }
    }

    if (best && bestScore >= 40) matched.push(best);
  }

  return matched.join(', ');
}

// Parse "last engaged" date from CSV.
// Accepts: DD/MM/YY, DD/MM/YYYY, MM/YY, MM/YYYY, "May 2026", "21 May 2026", "May-26",
//          YYYY-MM-DD, YYYY/MM/DD, YYYY/MM.
// Returns ISO date string YYYY-MM-DD (first of month for month-only input), or ''.
function parseLastEngaged(raw) {
  if (!raw) return '';
  const MONTHS = {
    jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
    jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
  };
  const s = raw.trim();

  // YYYY-MM-DD or YYYY/MM/DD (ISO-ish)
  const isoFull = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (isoFull) {
    return `${isoFull[1]}-${isoFull[2].padStart(2, '0')}-${isoFull[3].padStart(2, '0')}`;
  }
  // YYYY-MM or YYYY/MM
  const isoMy = s.match(/^(\d{4})[-/](\d{1,2})$/);
  if (isoMy) {
    return `${isoMy[1]}-${isoMy[2].padStart(2, '0')}-01`;
  }

  // Named month: "21 May 2026", "21 May 26", "May 2026", "May 26", "May-26", "May-2026"
  const named = s.match(/^(?:(\d{1,2})\s+)?([A-Za-z]{3,})[.\-\s]*(\d{2,4})$/);
  if (named) {
    const mon = MONTHS[named[2].toLowerCase().substring(0, 3)];
    if (mon) {
      const year = named[3].length === 2 ? '20' + named[3] : named[3];
      const day = named[1] ? named[1].padStart(2, '0') : '01';
      return `${year}-${mon}-${day}`;
    }
  }

  // Numeric separators — normalise . and - to /
  const n = s.replace(/[.-]/g, '/');
  // DD/MM/YY or DD/MM/YYYY
  const dmy = n.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, '0');
    const month = dmy[2].padStart(2, '0');
    const year = dmy[3].length === 2 ? '20' + dmy[3] : dmy[3];
    return `${year}-${month}-${day}`;
  }
  // MM/YY or MM/YYYY (no day → first of month)
  const my = n.match(/^(\d{1,2})\/(\d{2,4})$/);
  if (my) {
    const month = my[1].padStart(2, '0');
    const year = my[2].length === 2 ? '20' + my[2] : my[2];
    return `${year}-${month}-01`;
  }
  return '';
}

// Combine two text values — if both are non-empty and different, join with a newline.
function combineText(existing, incoming) {
  const e = (existing || '').trim();
  const i = (incoming || '').trim();
  if (!e) return i;
  if (!i || e === i) return e;
  return `${e}\n${i}`;
}

// Parse a rating value from CSV. Accepts "3/5", "3 / 10", plain "3", etc.
// Normalises to the app's 0-5 scale (rounds to nearest integer).
function parseRating(raw) {
  if (!raw) return 0;
  const s = String(raw).trim();
  const fraction = s.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
  if (fraction) {
    const num = parseFloat(fraction[1]);
    const denom = parseFloat(fraction[2]);
    if (denom > 0) return Math.min(5, Math.max(0, Math.round((num / denom) * 5)));
  }
  const plain = parseFloat(s);
  if (!isNaN(plain)) return Math.min(5, Math.max(0, Math.round(plain)));
  return 0;
}

function findDuplicateContact(row, vendorContacts) {
  const targetVendorId = row._selectedVendorId !== '__new__' ? Number(row._selectedVendorId) : null;
  if (!targetVendorId) return null;
  const nameLower = (row.name || '').trim().toLowerCase();
  if (!nameLower) return null;
  return (vendorContacts || []).find(c =>
    c.vendorId === targetVendorId &&
    (c.name || '').trim().toLowerCase() === nameLower
  ) || null;
}

function findAllDuplicates(vendors, vendorContacts) {
  const groups = [];

  // 1. Vendor pairs by company-name similarity (score >= 75)
  const seenVendorPairs = new Set();
  for (let i = 0; i < vendors.length; i++) {
    for (let j = i + 1; j < vendors.length; j++) {
      const a = vendors[i], b = vendors[j];
      const { score, level } = scoreCompanyMatch(a.companyName, b.companyName);
      if (score >= 75) {
        const key = `${a.id}-${b.id}`;
        if (!seenVendorPairs.has(key)) {
          seenVendorPairs.add(key);
          const reason = level === 'exact' ? 'Identical name'
            : level === 'close' ? 'Very similar name' : 'Similar name';
          groups.push({ type: 'vendor', a, b, score, level, matchReason: reason });
        }
      }
    }
  }

  // 2. Contact pairs by exact email match
  const seenContactPairs = new Set();
  const byEmail = {};
  for (const c of vendorContacts) {
    if (!c.email?.trim()) continue;
    const key = c.email.toLowerCase().trim();
    if (!byEmail[key]) byEmail[key] = [];
    byEmail[key].push(c);
  }
  for (const contacts of Object.values(byEmail)) {
    if (contacts.length < 2) continue;
    for (let i = 0; i < contacts.length; i++) {
      for (let j = i + 1; j < contacts.length; j++) {
        const a = contacts[i], b = contacts[j];
        const key = `${Math.min(a.id, b.id)}-${Math.max(a.id, b.id)}`;
        if (!seenContactPairs.has(key)) {
          seenContactPairs.add(key);
          groups.push({ type: 'contact', a, b, score: 100, level: 'exact', matchReason: 'Same email address' });
        }
      }
    }
  }

  // 3. Contact pairs by same name under same vendor
  const byNameVendor = {};
  for (const c of vendorContacts) {
    if (!c.name?.trim()) continue;
    const key = `${c.vendorId}:${c.name.toLowerCase().trim()}`;
    if (!byNameVendor[key]) byNameVendor[key] = [];
    byNameVendor[key].push(c);
  }
  for (const contacts of Object.values(byNameVendor)) {
    if (contacts.length < 2) continue;
    for (let i = 0; i < contacts.length; i++) {
      for (let j = i + 1; j < contacts.length; j++) {
        const a = contacts[i], b = contacts[j];
        const key = `${Math.min(a.id, b.id)}-${Math.max(a.id, b.id)}`;
        if (!seenContactPairs.has(key)) {
          seenContactPairs.add(key);
          groups.push({ type: 'contact', a, b, score: 90, level: 'close', matchReason: 'Same name at same company' });
        }
      }
    }
  }

  // Vendors first, then by score descending
  groups.sort((x, y) => {
    if (x.type !== y.type) return x.type === 'vendor' ? -1 : 1;
    return y.score - x.score;
  });
  return groups;
}

const VENDOR_MERGE_FIELDS = [
  { key: 'companyName',   label: 'Company Name' },
  { key: 'companyEmail',  label: 'Company Email' },
  { key: 'website',       label: 'Website' },
  { key: 'phone',         label: 'Phone' },
  { key: 'category',      label: 'Category' },
  { key: 'specialism',    label: 'Specialism' },
  { key: 'location',      label: 'Location' },
  { key: 'street',        label: 'Street' },
  { key: 'city',          label: 'City' },
  { key: 'state',         label: 'State / Province' },
  { key: 'country',       label: 'Country' },
  { key: 'postcode',      label: 'Postcode' },
  { key: 'instagram',     label: 'Instagram' },
  { key: 'notes',         label: 'Notes' },
  { key: 'paymentTerms',  label: 'Payment Terms' },
  { key: 'budget',        label: 'Budget' },
  { key: 'rating',        label: 'Rating' },
  { key: 'dropboxUrl',    label: 'Dropbox URL' },
  { key: 'lastEngaged',   label: 'Last Engaged' },
];

const CONTACT_MERGE_FIELDS = [
  { key: 'name',        label: 'Name' },
  { key: 'designation', label: 'Designation' },
  { key: 'email',       label: 'Email' },
  { key: 'phone',       label: 'Phone' },
  { key: 'altPhone',    label: 'Alt Phone' },
  { key: 'fax',         label: 'Fax' },
  { key: 'linkedin',    label: 'LinkedIn' },
  { key: 'instagram',   label: 'Instagram' },
  { key: 'notes',       label: 'Notes' },
];

function MergeFieldRow({ fieldKey, label, aVal, bVal, selection, onChange }) {
  const areSame = aVal.toLowerCase().trim() === bVal.toLowerCase().trim();
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '110px 1fr 1fr', gap: 8,
      padding: '5px 0', borderBottom: '1px solid rgba(229,229,229,0.5)',
      alignItems: 'flex-start',
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#777', paddingTop: 3, lineHeight: 1.3 }}>
        {label}
      </div>
      {/* Record A */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 5, cursor: areSame ? 'default' : 'pointer' }}>
        {!areSame && (
          <input
            type="radio" name={fieldKey} value="a" checked={selection === 'a'}
            onChange={() => onChange('a')}
            style={{ marginTop: 3, flexShrink: 0, accentColor: BRIO_GOLD }}
          />
        )}
        <span style={{
          fontSize: 11, lineHeight: 1.4, wordBreak: 'break-word',
          color: aVal ? '#222' : '#bbb', fontStyle: aVal ? 'normal' : 'italic',
          background: (!areSame && selection === 'a') ? 'rgba(159,144,42,0.1)' : 'transparent',
          borderRadius: 3, padding: '1px 4px',
        }}>
          {aVal || '(empty)'}
        </span>
      </label>
      {/* Record B */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 5, cursor: areSame ? 'default' : 'pointer' }}>
        {!areSame && (
          <input
            type="radio" name={fieldKey} value="b" checked={selection === 'b'}
            onChange={() => onChange('b')}
            style={{ marginTop: 3, flexShrink: 0, accentColor: BRIO_GOLD }}
          />
        )}
        <span style={{
          fontSize: 11, lineHeight: 1.4, wordBreak: 'break-word',
          color: bVal ? '#222' : '#bbb', fontStyle: bVal ? 'normal' : 'italic',
          background: (!areSame && selection === 'b') ? 'rgba(159,144,42,0.1)' : 'transparent',
          borderRadius: 3, padding: '1px 4px',
        }}>
          {bVal || '(empty)'}
        </span>
      </label>
    </div>
  );
}

function MergeContactsModal({ vendors, vendorContacts, onClose, onMergeVendors, onMergeContacts }) {
  const [step, setStep] = useState('scanning'); // 'scanning' | 'review' | 'done'
  const [pendingGroups, setPendingGroups] = useState([]);
  const [index, setIndex] = useState(0);
  const [selections, setSelections] = useState({});
  const [merging, setMerging] = useState(false);
  const [mergedCount, setMergedCount] = useState(0);
  const [error, setError] = useState('');

  // Snapshot detection on mount — intentionally not re-run on data changes
  useEffect(() => {
    const found = findAllDuplicates(vendors || [], vendorContacts || []);
    setPendingGroups(found);
    setStep(found.length === 0 ? 'done' : 'review');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentGroup = pendingGroups[index] || null;
  const total = pendingGroups.length;
  const fields = currentGroup
    ? (currentGroup.type === 'vendor' ? VENDOR_MERGE_FIELDS : CONTACT_MERGE_FIELDS)
    : [];
  const currentPairKey = currentGroup ? getPairKey(currentGroup) : null;

  // Auto-pick selections when the viewed pair changes
  useEffect(() => {
    if (!currentGroup) return;
    const auto = {};
    for (const f of fields) {
      const aVal = String(currentGroup.a[f.key] || '').trim();
      const bVal = String(currentGroup.b[f.key] || '').trim();
      if (aVal && !bVal) auto[f.key] = 'a';
      else if (!aVal && bVal) auto[f.key] = 'b';
      else auto[f.key] = 'a';
    }
    setSelections(auto);
    setError('');
  }, [currentPairKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transition to done when queue empties
  useEffect(() => {
    if (step === 'review' && pendingGroups.length === 0) setStep('done');
  }, [pendingGroups.length, step]);

  const removeCurrent = useCallback(() => {
    setPendingGroups(prev => prev.filter((_, i) => i !== index));
    setIndex(prev => (prev >= pendingGroups.length - 1 ? Math.max(0, pendingGroups.length - 2) : prev));
  }, [index, pendingGroups.length]);

  const handleMerge = async () => {
    if (!currentGroup) return;
    setMerging(true);
    setError('');
    try {
      const mergedFields = {};
      for (const f of fields) {
        const pick = selections[f.key] || 'a';
        mergedFields[f.key] = (pick === 'a' ? currentGroup.a : currentGroup.b)[f.key];
      }
      const result = currentGroup.type === 'vendor'
        ? await onMergeVendors(currentGroup.a, currentGroup.b, mergedFields)
        : await onMergeContacts(currentGroup.a, currentGroup.b, mergedFields);

      if (result?.success) {
        setMergedCount(c => c + 1);
        removeCurrent();
      } else {
        setError(result?.error || 'Merge failed. Please try again.');
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setMerging(false);
    }
  };

  const handleSkip = () => removeCurrent();

  // Derived display values
  const typeLabel = currentGroup?.type === 'vendor' ? 'Company' : 'Contact';
  const typeBadgeStyle = currentGroup?.type === 'vendor'
    ? { bg: 'rgba(104,149,129,0.12)', color: BRIO_GREEN }
    : { bg: 'rgba(219,107,93,0.1)', color: '#DB6B5D' };

  const bContacts = (currentGroup?.type === 'vendor')
    ? (vendorContacts || []).filter(c => c.vendorId === currentGroup.b.id)
    : null;

  const aVendorName = (currentGroup?.type === 'contact')
    ? ((vendors || []).find(v => v.id === currentGroup.a.vendorId)?.companyName || 'Unknown')
    : null;
  const bVendorName = (currentGroup?.type === 'contact')
    ? ((vendors || []).find(v => v.id === currentGroup.b.vendorId)?.companyName || 'Unknown')
    : null;

  const visibleFields = fields.filter(f => {
    const aVal = String(currentGroup?.a[f.key] || '').trim();
    const bVal = String(currentGroup?.b[f.key] || '').trim();
    return aVal || bVal;
  });

  return (
    <BrioModal title="Find & Merge Duplicates" onClose={onClose} width="xl">

      {/* ── Scanning ─────────────────────────────────────────────────── */}
      {step === 'scanning' && (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: FONT, color: '#666' }}>
          <Loader2 size={24} style={{ color: BRIO_GOLD, display: 'inline-block' }} />
          <div style={{ marginTop: 12, fontSize: 13 }}>Scanning for duplicates…</div>
        </div>
      )}

      {/* ── Review ───────────────────────────────────────────────────── */}
      {step === 'review' && currentGroup && (
        <div style={{ fontFamily: FONT }}>

          {/* Navigation bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '10px 16px', borderBottom: '1px solid ' + BRIO_BORDER,
          }}>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              background: typeBadgeStyle.bg, color: typeBadgeStyle.color,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {typeLabel}
            </span>
            <span style={{ fontSize: 11, color: '#555' }}>{currentGroup.matchReason}</span>
            <span style={{ fontSize: 10, color: '#aaa' }}>{currentGroup.score}% match</span>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => setIndex(i => Math.max(0, i - 1))}
                disabled={index === 0}
                style={{
                  padding: '3px 8px', fontSize: 13, fontFamily: FONT,
                  border: '1px solid ' + BRIO_BORDER, borderRadius: 4,
                  background: 'none', cursor: index === 0 ? 'default' : 'pointer',
                  color: index === 0 ? '#ccc' : '#555',
                }}
              >‹</button>
              <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>{index + 1} / {total}</span>
              <button
                onClick={() => setIndex(i => Math.min(total - 1, i + 1))}
                disabled={index >= total - 1}
                style={{
                  padding: '3px 8px', fontSize: 13, fontFamily: FONT,
                  border: '1px solid ' + BRIO_BORDER, borderRadius: 4,
                  background: 'none', cursor: index >= total - 1 ? 'default' : 'pointer',
                  color: index >= total - 1 ? '#ccc' : '#555',
                }}
              >›</button>
            </div>
          </div>

          {/* Column header row */}
          <div style={{
            display: 'grid', gridTemplateColumns: '110px 1fr 1fr',
            padding: '7px 16px', background: BRIO_BG, borderBottom: '1px solid ' + BRIO_BORDER,
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Field</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Record A — keep</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Record B — discard</div>
          </div>

          {/* Field comparison rows */}
          <div style={{ maxHeight: 320, overflowY: 'auto', padding: '0 16px' }}>
            {visibleFields.map(f => (
              <MergeFieldRow
                key={f.key}
                fieldKey={f.key}
                label={f.label}
                aVal={String(currentGroup.a[f.key] ?? '')}
                bVal={String(currentGroup.b[f.key] ?? '')}
                selection={selections[f.key] || 'a'}
                onChange={val => setSelections(prev => ({ ...prev, [f.key]: val }))}
              />
            ))}
            {visibleFields.length === 0 && (
              <div style={{ padding: '16px 0', fontSize: 11, color: '#aaa', textAlign: 'center' }}>
                All fields are identical.
              </div>
            )}
          </div>

          {/* Contacts being moved (vendor merges) */}
          {currentGroup.type === 'vendor' && bContacts && bContacts.length > 0 && (
            <div style={{
              margin: '8px 16px', padding: '8px 12px',
              background: BRIO_BG, borderRadius: 4, border: '1px solid ' + BRIO_BORDER,
              fontSize: 11, color: '#555',
            }}>
              <span style={{ fontWeight: 600 }}>{bContacts.length} contact{bContacts.length !== 1 ? 's' : ''}</span>
              {' '}from Record B will be moved to Record A:
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {bContacts.map(c => (
                  <span key={c.id} style={{
                    background: '#fff', border: '1px solid ' + BRIO_BORDER,
                    borderRadius: 3, padding: '1px 6px', fontSize: 10, color: '#666',
                  }}>
                    {c.name || c.email || 'Unnamed'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Parent company context (contact merges) */}
          {currentGroup.type === 'contact' && (
            <div style={{
              margin: '8px 16px', padding: '8px 12px',
              background: BRIO_BG, borderRadius: 4, border: '1px solid ' + BRIO_BORDER,
              fontSize: 11, color: '#555',
            }}>
              Record A is at <strong>{aVendorName}</strong>
              {currentGroup.a.vendorId !== currentGroup.b.vendorId && (
                <> · Record B is at <strong>{bVendorName}</strong></>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div style={{
              margin: '0 16px 4px', padding: '6px 10px',
              background: 'rgba(147,73,72,0.08)', border: '1px solid rgba(147,73,72,0.25)',
              borderRadius: 4, fontSize: 11, color: BRIO_RED,
            }}>
              {error}
            </div>
          )}

          {/* Footer */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderTop: '1px solid ' + BRIO_BORDER,
          }}>
            <button
              onClick={handleSkip}
              disabled={merging}
              style={{
                padding: '5px 14px', fontSize: 11, fontFamily: FONT,
                background: 'none', border: '1px solid ' + BRIO_BORDER,
                borderRadius: 4, cursor: 'pointer', color: '#555',
              }}
            >
              Not a duplicate
            </button>
            <button
              onClick={handleMerge}
              disabled={merging}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 16px', fontSize: 11, fontFamily: FONT, fontWeight: 600,
                background: BRIO_GOLD, color: '#fff', border: 'none',
                borderRadius: 4, cursor: merging ? 'wait' : 'pointer',
                opacity: merging ? 0.7 : 1,
              }}
            >
              {merging
                ? <Loader2 size={12} />
                : <GitMerge size={12} />}
              Merge into Record A
            </button>
          </div>
        </div>
      )}

      {/* ── Done ─────────────────────────────────────────────────────── */}
      {step === 'done' && (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: FONT }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(104,149,129,0.12)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 12,
          }}>
            <Check size={24} style={{ color: BRIO_GREEN }} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>
            {mergedCount > 0
              ? `${mergedCount} duplicate${mergedCount !== 1 ? 's' : ''} merged`
              : 'No duplicates found'}
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
            {mergedCount > 0
              ? 'Your contacts directory has been cleaned up.'
              : 'Your contacts directory looks clean.'}
          </div>
          <button
            onClick={onClose}
            style={{
              marginTop: 20, padding: '6px 24px', fontSize: 12, fontFamily: FONT, fontWeight: 600,
              background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      )}
    </BrioModal>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// TOOLBAR COMPONENTS (matching AdminTeamOverviewPage pattern)
// ══════════════════════════════════════════════════════════════════════════

function ToolbarIcon({ onClick, tooltip, active, badge, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '4px 6px',
        border: '1px solid ' + (active ? BRIO_GOLD : (hovered ? BRIO_GOLD : BRIO_BORDER)),
        borderRadius: '4px', background: 'transparent',
        color: active ? BRIO_GOLD : (hovered ? BRIO_GOLD : '#888'),
        cursor: 'pointer', transition: 'all 0.15s ease', position: 'relative',
        fontFamily: FONT, fontSize: '10px', fontWeight: 600, lineHeight: 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      {badge != null && <span style={{ fontSize: '9px', fontWeight: 500 }}>({badge})</span>}
      {hovered && tooltip && (
        <span style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: '6px', padding: '3px 8px', borderRadius: '3px',
          background: '#333', color: '#fff', fontSize: '10px', fontWeight: 400,
          whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 300, lineHeight: '14px',
        }}>{tooltip}</span>
      )}
    </button>
  );
}

function SortPanel({ sortRules, onChangeSortRules, onClose }) {
  const panelRef = useRef(null);
  useEffect(() => {
    const handle = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  const usedKeys = sortRules.map(r => r.key);
  const availableCols = SORTABLE_COLUMNS.filter(c => !usedKeys.includes(c.key));

  return (
    <div ref={panelRef} style={{
      position: 'absolute', top: '100%', right: 0, zIndex: 200,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      minWidth: 320, marginTop: 4, padding: 12, fontFamily: FONT,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sort</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 2 }}><X size={14} /></button>
      </div>
      {sortRules.map((rule, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: '11px', color: BRIO_GREY, minWidth: 28 }}>{idx === 0 ? 'by' : 'then'}</span>
          <select value={rule.key}
            onChange={e => { const next = [...sortRules]; next[idx] = { ...next[idx], key: e.target.value }; onChangeSortRules(next); }}
            style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT }}
          >
            {SORTABLE_COLUMNS.filter(c => c.key === rule.key || !usedKeys.includes(c.key)).map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <button
            onClick={() => { const next = [...sortRules]; next[idx] = { ...next[idx], dir: rule.dir === 'asc' ? 'desc' : 'asc' }; onChangeSortRules(next); }}
            style={{ background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, cursor: 'pointer', padding: '3px 6px', fontSize: '10px', color: BRIO_GOLD, fontFamily: FONT }}
          >{rule.dir === 'asc' ? '↑ Asc' : '↓ Desc'}</button>
          <button onClick={() => onChangeSortRules(sortRules.filter((_, i) => i !== idx))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_RED, padding: 2 }}><X size={12} /></button>
        </div>
      ))}
      {availableCols.length > 0 && (
        <button onClick={() => onChangeSortRules([...sortRules, { key: availableCols[0].key, dir: 'asc' }])}
          style={{ fontSize: '11px', color: BRIO_GOLD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 500, padding: '4px 0' }}>
          + Add sort rule
        </button>
      )}
    </div>
  );
}

function FilterPanel({ filterRules, onChangeFilterRules, onClose, uniqueValues }) {
  const panelRef = useRef(null);
  useEffect(() => {
    const handle = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  return (
    <div ref={panelRef} style={{
      position: 'absolute', top: '100%', right: 0, zIndex: 200,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      minWidth: 380, marginTop: 4, padding: 12, fontFamily: FONT,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 2 }}><X size={14} /></button>
      </div>
      {filterRules.map((rule, idx) => {
        const vals = uniqueValues[rule.key] || [];
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <select value={rule.key}
              onChange={e => { const next = [...filterRules]; next[idx] = { key: e.target.value, condition: 'is', value: '' }; onChangeFilterRules(next); }}
              style={{ fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT, minWidth: 90 }}
            >
              {FILTERABLE_COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select value={rule.condition}
              onChange={e => { const next = [...filterRules]; next[idx] = { ...next[idx], condition: e.target.value }; onChangeFilterRules(next); }}
              style={{ fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT }}
            >
              <option value="is">is</option>
              <option value="is not">is not</option>
              <option value="contains">contains</option>
            </select>
            {rule.condition === 'contains' ? (
              <input type="text" value={rule.value}
                onChange={e => { const next = [...filterRules]; next[idx] = { ...next[idx], value: e.target.value }; onChangeFilterRules(next); }}
                style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT }}
                placeholder="Search..." />
            ) : (
              <select value={rule.value}
                onChange={e => { const next = [...filterRules]; next[idx] = { ...next[idx], value: e.target.value }; onChangeFilterRules(next); }}
                style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT }}
              >
                <option value="">— Select —</option>
                {vals.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
            <button onClick={() => onChangeFilterRules(filterRules.filter((_, i) => i !== idx))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_RED, padding: 2 }}><X size={12} /></button>
          </div>
        );
      })}
      <button onClick={() => onChangeFilterRules([...filterRules, { key: FILTERABLE_COLUMNS[0].key, condition: 'is', value: '' }])}
        style={{ fontSize: '11px', color: BRIO_GOLD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 500, padding: '4px 0' }}>
        + Add filter rule
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// COLUMN DEFINITIONS
// ══════════════════════════════════════════════════════════════════════════

const COLUMNS = [
  { key: 'companyName',       label: 'Company',        minW: 120, flexW: 150, sortable: true,  filterable: true,  alwaysFrozen: true },
  { key: 'category',          label: 'Category',       minW: 90,  flexW: 110, sortable: true,  filterable: true  },
  { key: 'specialism',        label: 'Specialism',     minW: 100, flexW: 130, sortable: true,  filterable: true  },
  { key: 'website',           label: 'Website',        minW: 100, flexW: 120, sortable: false, filterable: false },
  { key: 'companyEmail',      label: 'Email',          minW: 110, flexW: 130, sortable: true,  filterable: false },
  { key: 'rating',            label: 'Rating',         minW: 60,  flexW: 70,  sortable: true,  filterable: false },
  { key: 'location',          label: 'Location',       minW: 85,  flexW: 100, sortable: true,  filterable: true  },
  { key: 'instagram',         label: 'Insta',          minW: 80,  flexW: 100, sortable: false, filterable: false },
  { key: 'dropboxUrl',        label: 'Dropbox',        minW: 80,  flexW: 100, sortable: false, filterable: false },
  { key: 'workedWithBefore',  label: 'Worked With',    minW: 100, flexW: 120, sortable: true,  filterable: false },
  { key: 'brioProject',       label: 'BRIO Project',   minW: 110, flexW: 140, sortable: true,  filterable: true  },
  { key: 'brioTeamInvolved',  label: 'BRIO Team',      minW: 110, flexW: 140, sortable: false, filterable: false },
  { key: 'phone',             label: 'Phone',          minW: 90,  flexW: 110, sortable: false, filterable: false },
  { key: 'lastEngaged',       label: 'Last Engaged',   minW: 85,  flexW: 95,  sortable: true,  filterable: false },
  { key: 'paymentTerms',      label: 'Pmt Terms',      minW: 80,  flexW: 95,  sortable: false, filterable: false },
  { key: 'budget',            label: 'Budget',         minW: 80,  flexW: 95,  sortable: true,  filterable: true  },
  { key: 'notes',             label: 'Notes',          minW: 120, flexW: 160, sortable: false, filterable: false },
  { key: 'dateAdded',         label: 'Date Added',     minW: 85,  flexW: 95,  sortable: true,  filterable: false },
  { key: 'commsEnabled',      label: 'Comms',          minW: 55,  flexW: 60,  sortable: false, filterable: false, mediaOnly: true },
];

const SORTABLE_COLUMNS = COLUMNS.filter(c => c.sortable);
const FILTERABLE_COLUMNS = COLUMNS.filter(c => c.filterable);

// ══════════════════════════════════════════════════════════════════════════
// COLUMN ORDER / FREEZE / COLLAPSE HELPERS
// ══════════════════════════════════════════════════════════════════════════

function loadStoredColumnOrder() {
  try {
    const saved = localStorage.getItem(CD_COL_ORDER_KEY);
    if (!saved) return null;
    const order = JSON.parse(saved);
    const defaultKeys = new Set(COLUMNS.map(c => c.key));
    if (!Array.isArray(order) || order.length !== COLUMNS.length) return null;
    if (!order.every(k => defaultKeys.has(k))) return null;
    return order;
  } catch { return null; }
}

function getOrderedColumns(order, frozenCols) {
  let cols;
  if (!order) {
    cols = [...COLUMNS];
  } else {
    const map = {};
    COLUMNS.forEach(c => { map[c.key] = c; });
    cols = order.map(k => map[k]).filter(Boolean);
  }
  const alwaysFrozen = cols.filter(c => c.alwaysFrozen);
  const userFrozen = cols.filter(c => !c.alwaysFrozen && frozenCols[c.key]);
  const rest = cols.filter(c => !c.alwaysFrozen && !frozenCols[c.key]);
  return [...alwaysFrozen, ...userFrozen, ...rest];
}

function loadFrozenCols() {
  try {
    const saved = localStorage.getItem(CD_FROZEN_COLS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
}

function computeFrozenLeft(columns, collapsedCols, frozenCols, colIdx) {
  let left = 0;
  for (let i = 0; i < colIdx; i++) {
    const c = columns[i];
    const isFr = c.alwaysFrozen || frozenCols[c.key];
    if (!isFr) continue;
    left += collapsedCols[c.key] ? COLLAPSED_W : c.flexW;
  }
  return left;
}

// ══════════════════════════════════════════════════════════════════════════
// FIXED DATE PICKER — wraps BrioDatePicker, repositions dropdown to fixed
// so it escapes overflowX:auto scroll containers
// ══════════════════════════════════════════════════════════════════════════

function FixedDatePicker({ value, onChange, style: extraStyle }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new MutationObserver(() => {
      const dropdown = el.querySelector('.brio-datepicker-dropdown');
      if (dropdown && dropdown.style.position !== 'fixed') {
        const trigger = el.querySelector('button, .brio-datepicker-trigger--compact');
        if (trigger) {
          const rect = trigger.getBoundingClientRect();
          const vh = window.innerHeight;
          dropdown.style.position = 'fixed';
          dropdown.style.left = rect.left + 'px';
          if (rect.bottom + 300 > vh) {
            dropdown.style.top = 'auto';
            dropdown.style.bottom = (vh - rect.top + 4) + 'px';
          } else {
            dropdown.style.top = rect.bottom + 4 + 'px';
            dropdown.style.bottom = 'auto';
          }
        }
      }
    });
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapRef}>
      <BrioDatePicker
        value={value || ''}
        onChange={v => onChange(v)}
        clearable
        className="brio-datepicker-trigger--compact"
        style={{ width: '100%', maxWidth: '95px', fontSize: '10px', boxSizing: 'border-box', ...extraStyle }}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// EDITABLE CELL
// ══════════════════════════════════════════════════════════════════════════

function EditableCell({ value, onChange, type, options, placeholder, style: extraStyle, displayValue, multiline }) {
  const readOnly = React.useContext(ContactsReadOnlyContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value || '')) onChange(draft);
  };

  // Read-only viewers: render plain, non-interactive text for every field type.
  if (readOnly) {
    return (
      <span
        style={{
          display: 'block', minHeight: '16px',
          color: value ? '#1e293b' : '#999',
          fontSize: '11px', lineHeight: '16px',
          overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
          ...extraStyle,
        }}
        title={value || ''}
      >
        {displayValue || value || '—'}
      </span>
    );
  }

  if (type === 'date') {
    return (
      <FixedDatePicker
        value={value || ''}
        onChange={v => onChange(v)}
        style={extraStyle}
      />
    );
  }

  if (!editing) {
    return (
      <span
        onClick={() => setEditing(true)}
        style={{
          cursor: 'pointer', display: 'block', minHeight: '16px',
          borderBottom: '1px dashed ' + BRIO_GOLD + '60',
          color: value ? '#1e293b' : '#999',
          fontSize: '11px', lineHeight: '16px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
          ...extraStyle,
        }}
        title={value || placeholder || 'Click to edit'}
      >
        {displayValue || value || placeholder || '—'}
      </span>
    );
  }

  if (type === 'select') {
    return (
      <select
        ref={inputRef}
        value={draft}
        onChange={e => { setDraft(e.target.value); }}
        onBlur={commit}
        style={{ fontSize: '11px', padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', fontFamily: FONT, width: '100%' }}
      >
        <option value="">—</option>
        {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  if (multiline) {
    return (
      <textarea
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        rows={3}
        style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', fontFamily: FONT, width: '100%', resize: 'vertical' }}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); }}
      style={{ fontSize: '11px', padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', fontFamily: FONT, width: '100%' }}
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════
// STAR RATING
// ══════════════════════════════════════════════════════════════════════════

function StarRating({ value, onChange }) {
  const readOnly = React.useContext(ContactsReadOnlyContext);
  const [hover, setHover] = useState(0);
  const rating = Number(value) || 0;
  return (
    <span style={{ display: 'inline-flex', gap: '1px', cursor: readOnly ? 'default' : 'pointer' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          size={13}
          fill={(hover || rating) >= n ? BRIO_GOLD : 'none'}
          stroke={(hover || rating) >= n ? BRIO_GOLD : '#ccc'}
          onMouseEnter={readOnly ? undefined : () => setHover(n)}
          onMouseLeave={readOnly ? undefined : () => setHover(0)}
          onClick={readOnly ? undefined : () => onChange(n === rating ? 0 : n)}
        />
      ))}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// NOTES POPOVER — fixed-position panel matching app tooltip style
// ══════════════════════════════════════════════════════════════════════════

function NotesDrawer({ vendorName, value, onChange, onClose }) {
  const isReadOnly = React.useContext(ContactsReadOnlyContext);
  const [draft, setDraft] = useState(value || '');
  const drawerRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        if (draft !== (value || '')) onChange(draft);
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, draft, value, onChange]);

  const handleSave = () => {
    if (draft !== (value || '')) onChange(draft);
    onClose();
  };

  return (
    <div ref={drawerRef} style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: '360px', maxWidth: '90vw',
      background: '#fff', borderLeft: '2px solid ' + BRIO_GOLD,
      boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
      zIndex: 500, overflowY: 'auto',
      fontFamily: FONT, padding: '20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Notes — {vendorName}</span>
        <button onClick={handleSave} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={16} /></button>
      </div>

      <textarea
        autoFocus={!isReadOnly}
        readOnly={isReadOnly}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        rows={14}
        style={{
          width: '100%', fontSize: '12px', fontFamily: FONT,
          padding: '10px 12px', border: '1px solid ' + BRIO_BORDER,
          borderRadius: 4, resize: 'vertical', boxSizing: 'border-box',
          color: '#1e293b', lineHeight: '1.6',
          ...(isReadOnly ? { background: '#f8f8f6' } : null),
        }}
        placeholder={isReadOnly ? 'No notes.' : 'Add notes about this company...'}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button
          onClick={() => { setDraft(value || ''); onClose(); }}
          style={{ fontSize: '11px', padding: '6px 16px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer', fontFamily: FONT, color: '#666' }}
        >{isReadOnly ? 'Close' : 'Cancel'}</button>
        {!isReadOnly && (
          <button
            onClick={handleSave}
            style={{ fontSize: '11px', padding: '6px 16px', background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: FONT, fontWeight: 500 }}
          >Save</button>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MULTI-SELECT DROPDOWN — native <select> style, accumulates selections
// ══════════════════════════════════════════════════════════════════════════

function MultiSelectDropdown({ value, options, onChange, placeholder }) {
  const readOnly = React.useContext(ContactsReadOnlyContext);
  const [editing, setEditing] = useState(false);
  const containerRef = useRef(null);

  const selected = useMemo(() =>
    (value || '').split(',').map(s => s.trim()).filter(Boolean),
    [value]
  );

  // Close on click outside
  useEffect(() => {
    if (!editing) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setEditing(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing]);

  const available = useMemo(() =>
    (options || []).filter(o => !selected.includes(o)),
    [options, selected]
  );

  const handleAdd = (item) => {
    if (!item) return;
    onChange([...selected, item].join(', '));
  };

  const handleRemove = (item) => {
    onChange(selected.filter(s => s !== item).join(', '));
  };

  if (readOnly || !editing) {
    return (
      <span
        onClick={readOnly ? undefined : () => setEditing(true)}
        style={{
          cursor: readOnly ? 'default' : 'pointer', display: 'block', minHeight: '16px',
          borderBottom: readOnly ? 'none' : '1px dashed ' + BRIO_GOLD + '60',
          color: selected.length ? '#1e293b' : '#999',
          fontSize: '11px', lineHeight: '16px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        title={selected.length ? selected.join(', ') : (placeholder || (readOnly ? '' : 'Click to select'))}
      >
        {selected.length ? selected.join(', ') : (placeholder || '—')}
      </span>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Selected items as removable tags */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 3 }}>
          {selected.map(s => (
            <span key={s} style={{
              display: 'inline-flex', alignItems: 'center', gap: 2,
              fontSize: '9px', padding: '1px 5px', borderRadius: 3,
              background: '#f0ede0', color: '#555', fontFamily: FONT,
            }}>
              <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s}>{s}</span>
              <X size={8} style={{ flexShrink: 0, cursor: 'pointer' }}
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleRemove(s); }} />
            </span>
          ))}
        </div>
      )}
      {/* Add dropdown — stays open until click outside */}
      <select
        autoFocus
        value=""
        onChange={e => { if (e.target.value) handleAdd(e.target.value); e.target.value = ''; }}
        style={{ fontSize: '11px', padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', fontFamily: FONT, width: '100%' }}
      >
        <option value="">{available.length ? '+ Add…' : '✓ All selected'}</option>
        {available.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// LOCATION BADGE — shows "city, state, country" badge; click to open
// structured address popover (city / state / country / address / postcode)
// ══════════════════════════════════════════════════════════════════════════

function LocationBadge({ vendor, onSave }) {
  const readOnly = React.useContext(ContactsReadOnlyContext);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const badgeRef = useRef(null);
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const labelParts = [vendor.city, vendor.state, vendor.country].filter(Boolean);
  const label = labelParts.join(', ');

  const openPopover = (e) => {
    e.stopPropagation();
    setDraft({
      city: vendor.city || '',
      state: vendor.state || '',
      country: vendor.country || '',
      street: vendor.street || '',
      postcode: vendor.postcode || '',
    });
    const rect = badgeRef.current?.getBoundingClientRect();
    if (rect) {
      const top = rect.bottom + window.scrollY + 4;
      const left = Math.min(rect.left + window.scrollX, window.innerWidth - 260);
      setPos({ top, left });
    }
    setOpen(true);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          badgeRef.current && !badgeRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    const location = [draft.city, draft.country].filter(Boolean).join(', ');
    await onSave({ ...draft, location });
    setSaving(false);
    setOpen(false);
  };

  const fieldStyle = {
    fontSize: '11px', fontFamily: FONT, border: '1px solid ' + BRIO_BORDER,
    borderRadius: 3, padding: '3px 6px', width: '100%', boxSizing: 'border-box',
    outline: 'none', background: '#fff', color: '#1e293b',
  };
  const labelStyle = { fontSize: '10px', color: '#6b7280', fontFamily: FONT, marginBottom: 2, display: 'block' };

  return (
    <>
      <span
        ref={badgeRef}
        onClick={readOnly ? undefined : openPopover}
        style={{
          display: 'inline-block', cursor: readOnly ? 'default' : 'pointer',
          fontSize: '10px', lineHeight: '16px',
          padding: label ? '1px 6px' : '1px 4px',
          borderRadius: 10,
          background: label ? 'rgba(159,144,42,0.1)' : 'transparent',
          color: label ? '#7a6e22' : '#999',
          border: label ? '1px solid rgba(159,144,42,0.25)' : (readOnly ? 'none' : '1px dashed #ccc'),
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}
        title={[vendor.street, vendor.city, vendor.state, vendor.postcode, vendor.country].filter(Boolean).join(', ') || (readOnly ? '' : 'Click to add location')}
      >
        {label || (readOnly ? '—' : '+')}
      </span>

      {open && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'absolute', top: pos.top, left: pos.left,
            width: 250, background: '#fff',
            border: '1px solid ' + BRIO_BORDER,
            borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: 12, zIndex: 9999, fontFamily: FONT,
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'grid', gap: 8 }}>
            {[
              { key: 'city',     label: 'City' },
              { key: 'state',    label: 'State' },
              { key: 'country',  label: 'Country' },
              { key: 'street',   label: 'Address', multiline: true },
              { key: 'postcode', label: 'Postcode' },
            ].map(({ key, label: fl, multiline }) => (
              <div key={key}>
                <span style={labelStyle}>{fl}</span>
                {multiline ? (
                  <textarea
                    value={draft[key] || ''}
                    onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
                    rows={3}
                    style={{ ...fieldStyle, resize: 'vertical', minHeight: 54, lineHeight: '1.4' }}
                  />
                ) : (
                  <input
                    type="text"
                    value={draft[key] || ''}
                    onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setOpen(false); }}
                    style={fieldStyle}
                  />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
            <button onClick={() => setOpen(false)}
              style={{ fontSize: '10px', padding: '3px 10px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, background: '#fff', cursor: 'pointer', fontFamily: FONT }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ fontSize: '10px', padding: '3px 10px', border: 'none', borderRadius: 3, background: BRIO_GOLD, color: '#fff', cursor: saving ? 'wait' : 'pointer', fontFamily: FONT, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// COMPANY NAME CELL — double-click to edit, clipped with hover expand
// ══════════════════════════════════════════════════════════════════════════

function CompanyNameCell({ value, onChange }) {
  const readOnly = React.useContext(ContactsReadOnlyContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft !== (value || '')) onChange(draft.trim());
    else setDraft(value || '');
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
        onClick={e => e.stopPropagation()}
        style={{
          fontSize: '11px', fontWeight: 600, fontFamily: FONT, color: BRIO_GOLD,
          border: '1px solid ' + BRIO_GOLD, borderRadius: 2, padding: '1px 4px',
          width: '100%', boxSizing: 'border-box', outline: 'none', background: '#fff',
        }}
      />
    );
  }

  return (
    <span
      onDoubleClick={readOnly ? undefined : e => { e.stopPropagation(); setEditing(true); }}
      title={value || '—'}
      style={{
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        display: 'block', minWidth: 0, position: 'relative',
      }}
      onMouseEnter={e => {
        const el = e.currentTarget;
        if (el.scrollWidth > el.clientWidth) {
          el.style.overflow = 'visible';
          el.style.background = '#fff';
          el.style.zIndex = '5';
          el.style.paddingRight = '6px';
        }
      }}
      onMouseLeave={e => {
        const el = e.currentTarget;
        el.style.overflow = 'hidden';
        el.style.background = 'transparent';
        el.style.zIndex = '';
        el.style.paddingRight = '';
      }}
    >
      {value || '—'}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SPECIALISM AUTO-SUGGEST — input with suggestions from existing values
// ══════════════════════════════════════════════════════════════════════════

function SpecialismSelect({ value, options, onChange, onAddOption, vendorCategory, placeholder }) {
  const readOnly = React.useContext(ContactsReadOnlyContext);
  // value is a comma-separated string; parse to array for multi-select
  const selected = useMemo(() => (value || '').split(',').map(s => s.trim()).filter(Boolean), [value]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false); setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  const commitSelection = (arr) => onChange(arr.join(', '));

  const toggleOption = (label) => {
    const next = selectedSet.has(label) ? selected.filter(s => s !== label) : [...selected, label];
    commitSelection(next);
  };

  const removeTag = (label, e) => { e.stopPropagation(); commitSelection(selected.filter(s => s !== label)); };

  // Inline autocomplete: prefix-match while typing
  const handleSearchChange = (e) => {
    const typed = e.target.value;
    setSearch(typed);
    if (!typed) return;
    const q = typed.toLowerCase();
    const prefixMatch = options.find(s => s.toLowerCase().startsWith(q) && !selectedSet.has(s));
    if (prefixMatch) {
      setSearch(prefixMatch);
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.setSelectionRange(typed.length, prefixMatch.length);
      });
    }
  };

  const handleAddNew = async () => {
    const trimmed = search.trim();
    if (!trimmed || !onAddOption) return;
    setAddError(''); setAddLoading(true);
    const result = await onAddOption(trimmed, vendorCategory);
    setAddLoading(false);
    if (result.success) { commitSelection([...selected, result.option?.label || trimmed]); setSearch(''); }
    else if (result.existingLabel) { commitSelection([...selected, result.existingLabel]); setSearch(''); }
    else setAddError(result.error || 'Failed to add');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = search.trim();
      if (!trimmed) return;
      const match = options.find(s => s.toLowerCase() === trimmed.toLowerCase());
      if (match) { toggleOption(match); setSearch(''); }
      else if (vendorCategory) handleAddNew();
    } else if (e.key === 'Escape') { setIsOpen(false); setSearch(''); }
    else if (e.key === 'Backspace' && !search && selected.length > 0) commitSelection(selected.slice(0, -1));
  };

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(s => s.toLowerCase().includes(q));
  }, [options, search]);

  const hasExactMatch = useMemo(() => {
    if (!search.trim()) return true;
    return options.some(s => s.toLowerCase() === search.trim().toLowerCase());
  }, [options, search]);

  const getDropdownStyle = () => {
    const style = {
      position: 'fixed', zIndex: 10000, maxHeight: 250,
      display: 'flex', flexDirection: 'column',
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontFamily: FONT,
    };
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      style.left = rect.left + 'px';
      style.width = Math.max(rect.width, 200) + 'px';
      const vh = window.innerHeight;
      if (rect.bottom + 260 > vh) style.bottom = (vh - rect.top + 2) + 'px';
      else style.top = rect.bottom + 'px';
    }
    return style;
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', fontFamily: FONT }}>
      {/* Tags + input */}
      <div
        onClick={readOnly ? undefined : () => { if (!isOpen) setIsOpen(true); }}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center',
          minHeight: '16px', cursor: readOnly ? 'default' : 'pointer',
          borderBottom: readOnly ? 'none' : '1px dashed ' + BRIO_GOLD + '60', padding: '1px 0',
        }}
      >
        {selected.map(tag => (
          <span key={tag} style={{
            display: 'inline-flex', alignItems: 'center', gap: 2,
            padding: '0 4px', fontSize: '9px', fontWeight: 500, lineHeight: '16px',
            background: 'rgba(159,144,42,0.1)', color: BRIO_GOLD, borderRadius: 2,
          }}>
            {tag}
            {!readOnly && <X size={8} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={e => removeTag(tag, e)} />}
          </span>
        ))}
        {selected.length === 0 && !isOpen && (
          <span style={{ fontSize: '11px', color: '#999' }}>{placeholder || '—'}</span>
        )}
        {isOpen && (
          <input
            ref={inputRef} type="text" value={search}
            onChange={handleSearchChange} onKeyDown={handleKeyDown}
            placeholder={selected.length > 0 ? '' : (placeholder || 'Type to search...')}
            style={{ flex: 1, minWidth: 60, fontSize: '11px', border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT, padding: 0 }}
          />
        )}
      </div>

      {/* Dropdown — rendered via portal to escape table overflow/stacking contexts */}
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={getDropdownStyle()}>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(label => {
              const checked = selectedSet.has(label);
              return (
                <div key={label}
                  onMouseDown={e => { e.preventDefault(); toggleOption(label); setSearch(''); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', fontSize: '10px', cursor: 'pointer', background: checked ? 'rgba(159,144,42,0.1)' : 'transparent' }}
                  onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#f5f5f3'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = checked ? 'rgba(159,144,42,0.1)' : 'transparent'; }}
                >
                  <div style={{
                    width: 12, height: 12, border: '1.5px solid ' + (checked ? BRIO_GOLD : '#cbd5e1'),
                    borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: checked ? BRIO_GOLD : 'transparent', flexShrink: 0,
                  }}>
                    {checked && <Check size={8} color="white" strokeWidth={3} />}
                  </div>
                  <span style={{ color: checked ? BRIO_GOLD : '#374151' }}>{label}</span>
                </div>
              );
            })}
            {filtered.length === 0 && !search.trim() && (
              <div style={{ padding: '6px 8px', fontSize: '10px', color: '#9ca3af' }}>No options available</div>
            )}
          </div>
          {search.trim() && !hasExactMatch && (
            <div style={{ borderTop: '1px solid ' + BRIO_BORDER, padding: '3px 6px' }}>
              {vendorCategory ? (
                <div
                  onMouseDown={e => { e.preventDefault(); handleAddNew(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 2px', cursor: addLoading ? 'wait' : 'pointer', fontSize: '10px', fontWeight: 500, color: BRIO_GOLD }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(159,144,42,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {addLoading ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                  <span>Add "{search.trim()}"</span>
                </div>
              ) : (
                <div style={{ padding: '3px 2px', fontSize: '10px', color: '#999' }}>Set a category first to add new specialisms</div>
              )}
              {addError && <div style={{ fontSize: '9px', color: '#ef4444', marginTop: 1 }}>{addError}</div>}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// COMPANY NAME AUTOCOMPLETE — search-as-you-type to prevent duplicates
// ══════════════════════════════════════════════════════════════════════════

function CompanyNameAutocomplete({ value, onChange, vendors, onSelectExisting, placeholder, autoFocus, style }) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Click-outside handler
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Close on scroll (portal is position:fixed)
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => setIsOpen(false);
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [isOpen]);

  // Fuzzy matches
  const fuzzyMatches = useMemo(() => {
    if (!value || value.trim().length < 2) return [];
    return findMatchingVendors(value, vendors, { limit: 6, minScore: 60 });
  }, [value, vendors]);

  // Also include simple substring matches not caught by fuzzy scoring
  const allSuggestions = useMemo(() => {
    if (!value || value.trim().length < 2) return [];
    const q = value.toLowerCase().trim();
    const fuzzyIds = new Set(fuzzyMatches.map(m => m.vendor.id));
    const substringMatches = (vendors || [])
      .filter(v => !fuzzyIds.has(v.id) && v.companyName?.toLowerCase().includes(q))
      .slice(0, 4)
      .map(v => ({ vendor: v, score: 50, level: 'substring', distance: 0 }));
    return [...fuzzyMatches, ...substringMatches].slice(0, 8);
  }, [fuzzyMatches, vendors, value]);

  const showDropdown = isOpen && value?.trim().length >= 2;

  const handleChange = (e) => {
    onChange(e.target.value);
    setIsOpen(true);
    setHighlightIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || allSuggestions.length === 0) return;
    const maxIdx = allSuggestions.length; // last index = "Create new"
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, maxIdx));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      if (highlightIdx < allSuggestions.length) {
        onSelectExisting(allSuggestions[highlightIdx].vendor);
      }
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const getDropdownStyle = () => {
    const s = {
      position: 'fixed', zIndex: 10000,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontFamily: FONT,
      borderRadius: 4, maxHeight: 280, overflowY: 'auto',
    };
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      s.left = rect.left + 'px';
      s.width = Math.max(rect.width, 250) + 'px';
      const vh = window.innerHeight;
      if (rect.bottom + 290 > vh) s.bottom = (vh - rect.top + 2) + 'px';
      else s.top = rect.bottom + 'px';
    }
    return s;
  };

  const levelLabel = (level) =>
    level === 'exact' ? 'exact match'
    : level === 'close' ? 'likely match'
    : 'possible';

  const levelColor = (level) =>
    level === 'exact' ? BRIO_GREEN
    : level === 'close' ? '#b45309'
    : '#999';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        autoFocus={autoFocus}
        value={value || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (value?.trim().length >= 2) setIsOpen(true); }}
        placeholder={placeholder}
        style={style}
      />
      {showDropdown && (allSuggestions.length > 0) && ReactDOM.createPortal(
        <div ref={dropdownRef} style={getDropdownStyle()}>
          {allSuggestions.map((item, idx) => (
            <div
              key={item.vendor.id}
              onMouseDown={e => { e.preventDefault(); onSelectExisting(item.vendor); setIsOpen(false); }}
              onMouseEnter={() => setHighlightIdx(idx)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '5px 8px', fontSize: '11px', fontFamily: FONT,
                cursor: 'pointer',
                background: idx === highlightIdx ? '#f5f3e8' : 'transparent',
              }}
            >
              <span style={{ fontWeight: 500, color: '#1e293b' }}>
                {item.vendor.companyName}
              </span>
              <span style={{ fontSize: '9px', fontWeight: 500, color: levelColor(item.level), flexShrink: 0, marginLeft: 8 }}>
                {levelLabel(item.level)}
              </span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid ' + BRIO_BORDER, padding: '5px 8px' }}>
            <div
              onMouseDown={e => { e.preventDefault(); setIsOpen(false); }}
              onMouseEnter={() => setHighlightIdx(allSuggestions.length)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: '10px', fontWeight: 500, color: BRIO_GOLD,
                cursor: 'pointer', padding: '2px 0',
                background: highlightIdx === allSuggestions.length ? '#f5f3e8' : 'transparent',
                borderRadius: 2, marginLeft: -4, marginRight: -4, paddingLeft: 4,
              }}
            >
              <Plus size={11} />
              <span>Create new: &ldquo;{value}&rdquo;</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// INLINE CONTACTS ROW — sub-row below the company showing contact people
// ══════════════════════════════════════════════════════════════════════════

function InlineContactsRow({ colSpan, contacts, vendors, currentVendorId, onAddContact, onUpdateContact, onDeleteContact, onMoveContact, onCreateAndMoveContact, designationSuggestions, frozenLeft }) {
  const readOnly = React.useContext(ContactsReadOnlyContext);
  return (
    <tr>
      {/* Frozen spacer cell matching the company name column */}
      <td style={{
        padding: 0, borderBottom: '1px solid ' + BRIO_BORDER,
        background: 'var(--brio-surface-accent, #f5f3e8)',
        position: 'sticky', left: 0, zIndex: 5,
        borderRight: '2px solid ' + BRIO_GOLD + '40',
      }} />
      <td colSpan={colSpan - 1} style={{
        padding: 0, borderBottom: '1px solid ' + BRIO_BORDER,
        background: 'var(--brio-surface-accent, #f5f3e8)',
      }}>
        <div style={{ padding: '8px 16px 10px 32px', fontFamily: FONT }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: '#888', marginBottom: 6, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
            Contact People
          </div>

          {contacts.length === 0 && (
            <p style={{ fontSize: '11px', color: '#999', margin: '4px 0 8px' }}>No contacts added yet.</p>
          )}

          {/* Contacts table */}
          {contacts.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
              <thead>
                <tr>
                  {['Name', 'Designation', 'Phone', 'Alt. Phone', 'Email', 'LinkedIn', 'Insta', 'Notes', 'Move To', ''].map((h, i, arr) => (
                    <th key={i} style={{
                      fontSize: '9px', fontWeight: 600, color: '#999', textAlign: 'left',
                      padding: '2px 6px', borderBottom: '1px solid ' + BRIO_BORDER,
                      textTransform: 'uppercase', letterSpacing: '0.03em',
                      ...(i === arr.length - 1 ? {
                        position: 'sticky', right: 0,
                        background: 'var(--brio-surface-accent, #f5f3e8)',
                        borderLeft: '1px solid ' + BRIO_BORDER,
                      } : {}),
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id} onMouseEnter={e => { e.currentTarget.style.background = '#f0ede0'; }} onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '3px 6px', fontSize: '11px', fontWeight: 500, borderBottom: '1px solid #eee' }}>
                      <EditableCell value={c.name} onChange={v => onUpdateContact(c.id, 'name', v)} placeholder="Name" />
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid #eee', overflow: 'visible', position: 'relative' }}>
                      <EditableCell value={c.designation} onChange={v => onUpdateContact(c.id, 'designation', v)} placeholder="e.g. Director" />
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid #eee' }}>
                      <EditableCell value={c.phone} onChange={v => onUpdateContact(c.id, 'phone', v)} placeholder="+91..." />
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid #eee' }}>
                      <EditableCell value={c.altPhone} onChange={v => onUpdateContact(c.id, 'altPhone', v)} placeholder="—" />
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid #eee' }}>
                      <EditableCell value={c.email} onChange={v => onUpdateContact(c.id, 'email', v)} placeholder="email" />
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid #eee' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <EditableCell value={c.linkedin} onChange={v => onUpdateContact(c.id, 'linkedin', cleanLinkedinUrl(v))} placeholder="URL or username" style={{ flex: 1 }} />
                        {c.linkedin && (
                          <a href={cleanLinkedinUrl(c.linkedin)} target="_blank" rel="noopener noreferrer" style={{ color: BRIO_GOLD, flexShrink: 0 }}>
                            <Linkedin size={11} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid #eee' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <EditableCell value={c.instagram} onChange={v => onUpdateContact(c.id, 'instagram', v)} placeholder="@handle" style={{ flex: 1 }} />
                        {c.instagram && (
                          <a href={c.instagram.startsWith('http') ? c.instagram : `https://instagram.com/${c.instagram.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: BRIO_GOLD, flexShrink: 0 }}>
                            <Instagram size={11} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid #eee', minWidth: 100, maxWidth: 180 }}>
                      <EditableCell value={c.notes} onChange={v => onUpdateContact(c.id, 'notes', v)} placeholder="Add note..." />
                    </td>
                    <td style={{ padding: '3px 6px', borderBottom: '1px solid #eee', minWidth: 120 }}>
                      {readOnly ? (
                        <span style={{ fontSize: '10px', color: '#bbb' }}>—</span>
                      ) : (
                        <select
                          value=""
                          onChange={e => {
                            const val = e.target.value;
                            if (val === '__new__') {
                              const companyName = window.prompt(`Create a new company for "${c.name || 'this contact'}".\nCompany name:`);
                              if (companyName && companyName.trim()) {
                                onCreateAndMoveContact(c, companyName.trim());
                              }
                            } else {
                              const targetId = Number(val);
                              if (targetId && window.confirm(`Move "${c.name || 'this contact'}" to a different company?`)) {
                                onMoveContact(c.id, targetId);
                              }
                            }
                          }}
                          style={{ fontSize: '10px', padding: '1px 3px', width: '100%', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, color: '#999' }}
                        >
                          <option value="">Move to...</option>
                          <option value="__new__">+ Create new company</option>
                          {(vendors || []).filter(v => v.id !== currentVendorId).map(v => (
                            <option key={v.id} value={v.id}>{v.companyName}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '3px 6px', textAlign: 'center', borderBottom: '1px solid #eee', position: 'sticky', right: 0, background: 'var(--brio-surface-accent, #f5f3e8)', borderLeft: '1px solid ' + BRIO_BORDER }}>
                      {!readOnly && (
                        <button
                          onClick={() => { if (window.confirm('Remove this contact?')) onDeleteContact(c.id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: BRIO_RED }}
                          title="Remove contact"
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!readOnly && (
            <button
              onClick={onAddContact}
              style={{
                padding: '4px 10px', background: 'none',
                border: '1px dashed ' + BRIO_GOLD, borderRadius: 4,
                cursor: 'pointer', color: BRIO_GOLD,
                fontSize: '10px', fontWeight: 500, fontFamily: FONT,
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}
            >
              <Plus size={10} /> Add Contact Person
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CSV IMPORT HELPERS
// ══════════════════════════════════════════════════════════════════════════

/**
 * Full RFC 4180 document parser — correctly handles newlines inside quoted
 * fields (e.g. multiline notes with bullet points). Returns an array of rows,
 * each row being an array of field strings.
 */
function parseCSVDocument(text, delimiter) {
  const rows = [];
  const n = text.length;
  let i = 0;

  while (i < n) {
    const fields = [];
    let field = '';
    let inQuotes = false;

    while (i < n) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 2; } // escaped quote
        else if (ch === '"') { inQuotes = false; i++; }                  // end quote
        else { field += ch; i++; }                                        // content (including \n)
      } else {
        if (ch === '"') { inQuotes = true; i++; }
        else if (ch === delimiter) { fields.push(field.trim()); field = ''; i++; }
        else if (ch === '\r') { if (text[i + 1] === '\n') i++; i++; break; } // \r\n or bare \r
        else if (ch === '\n') { i++; break; }                              // end of row
        else { field += ch; i++; }
      }
    }

    fields.push(field.trim());
    if (fields.some(f => f)) rows.push(fields); // skip fully-empty rows
  }

  return rows;
}

function detectDelimiter(text) {
  const sample = text.split('\n').slice(0, 10).join('\n');
  // Strip quoted content for delimiter counting
  const stripped = sample.replace(/"[^"]*"/g, '');
  const tabs = (stripped.match(/\t/g) || []).length;
  const semis = (stripped.match(/;/g) || []).length;
  const commas = (stripped.match(/,/g) || []).length;
  if (tabs > semis && tabs > commas) return '\t';
  if (semis > commas) return ';';
  return ',';
}

/**
 * Detect Apple Contacts CSV format by checking header row for known columns.
 * Apple exports: First,Last,Company,Phone 1 - Value,Phone 2 - Value,
 *   E-mail 1 - Value,E-mail 2 - Value,Title,URL,Note,...
 * Also handles: "First Name","Last Name","Organization","Phone","Email",...
 */
function detectAppleContactsHeaders(headers) {
  const h = headers.map(s => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim());
  // Build a field map: normalized header → column index
  const map = {};

  // First / Last / Display name
  h.forEach((col, i) => {
    if (/^first( name)?$/.test(col)) map.firstName = i;
    else if (/^last( name)?$/.test(col)) map.lastName = i;
    else if (/^(middle|middle name)$/.test(col)) map.middleName = i;
    else if (/^(display name|full name|name)$/.test(col) && !map.displayName) map.displayName = i;
    // Company / Organisation
    else if (/^(company|organization|organisation|org)$/.test(col)) map.company = i;
    // Title / Designation / Job Title
    else if (/^(title|job title|designation|position|role)$/.test(col)) map.designation = i;
    // Phones — Apple uses "Phone 1 - Value", generic uses "Phone", "Mobile", "Work Phone"
    // Fax — separate field for fax numbers
    else if (/phone.*fax|fax/.test(col)) {
      if (!map.fax) map.fax = i;
    }
    // Company phone — "Phone: Company" (normalizes to "phone company") or "Company phone"
    // → stored on the vendor record (vendors.phone), NOT the contact person
    else if (/company.*phone/.test(col) || /phone.*company/.test(col)) {
      if (!map.companyPhone) map.companyPhone = i;
    }
    // Alt / alternate contact phone — checked BEFORE primary phone so "Phone: Mobile 2",
    // "Phone: Alt Contact", etc. are not swallowed by the mobile/primary pattern below.
    // Matches: "Phone: 2", "Phone: Mobile 2", "Phone: Alt", "Phone: Alt Contact",
    //          "Phone: Alternate Contact", "Alt Phone", "Alternate Phone"
    else if (
      /phone.*(2\b|alt|alternate)/.test(col) ||
      /(alt|alternate)\s*phone/.test(col) ||
      col === 'alt phone' || col === 'alternate phone'
    ) {
      if (!map.altPhone) map.altPhone = i;
    }
    else if (/phone.*(1|mobile|primary|value)/.test(col) || col === 'phone' || col === 'mobile') {
      if (!map.phone) map.phone = i;
      else if (!map.altPhone) map.altPhone = i;
    }
    else if (/phone.*(home|work|other)/.test(col) || col === 'work phone' || col === 'home phone') {
      if (!map.altPhone) map.altPhone = i;
    }
    // Company email — "Email: Company" (normalizes to "email company") or "company email"
    // → stored on the vendor record (vendors.company_email), NOT the contact person
    else if (/company.*e?mail/.test(col) || /e?mail.*company/.test(col)) {
      if (!map.companyEmail) map.companyEmail = i;
    }
    // Contact person email — "Email : work", "Email 1 - Value", plain "Email", etc.
    else if (/e?mail.*(1|primary|value|work)/.test(col) || col === 'email' || col === 'email address' || col === 'email1') {
      if (!map.email) map.email = i;
    }
    else if (/e?mail.*(2|other|secondary)/.test(col) || col === 'email2') {
      if (!map.email2) map.email2 = i;
    }
    // Website / URL
    else if (/^(url|website|web|homepage)/.test(col) || /\b(url|website|homepage)\b/.test(col)) map.website = i;
    // LinkedIn
    else if (/linkedin/.test(col)) map.linkedin = i;
    // Instagram — company first, then contact person, then plain "instagram" falls to contact
    else if (/company.*insta|insta.*company/.test(col)) {
      if (!map.companyInstagram) map.companyInstagram = i;
    }
    else if (/contact.*insta|insta.*contact|instagram/.test(col)) {
      if (!map.instagram) map.instagram = i;
    }
    // Worked with
    else if (/worked\s*with/.test(col)) {
      if (!map.workedWithBefore) map.workedWithBefore = i;
    }
    // BRIO Project — "brio project", "BRIO Project", "brioproject"
    else if (/brio\s*project/.test(col)) {
      if (!map.brioProject) map.brioProject = i;
    }
    // BRIO Team — "brio team", "BRIO Team", "brioteam"
    else if (/brio\s*team/.test(col)) {
      if (!map.brioTeamInvolved) map.brioTeamInvolved = i;
    }
    // Dropbox — "Dropbox", "Dropbox link", "Dropbox URL"
    else if (/dropbox/.test(col)) {
      if (!map.dropboxUrl) map.dropboxUrl = i;
    }
    // Category — "category", "categories", "catorgories" (common misspelling)
    else if (/^categor|^catorgor|^categ/.test(col)) {
      if (!map.category) map.category = i;
    }
    // Last Engaged — "last engaged", "last engagement", "lastengaged"
    else if (/^last\s*engage/.test(col)) {
      if (!map.lastEngaged) map.lastEngaged = i;
    }
    // Budget — "budget" (single select: High / Mid / Low)
    else if (/^budget/.test(col)) {
      if (!map.budget) map.budget = i;
    }
    // Contact-person notes — "Notes: Contact", "Contact notes"
    else if (/contact.*notes?|notes?.*contact/.test(col)) {
      if (!map.contactNotes) map.contactNotes = i;
    }
    // Company notes — "Notes: Company", "Company notes", "notes company"
    else if (/company.*notes?|notes?.*company/.test(col)) {
      if (!map.companyNotes) map.companyNotes = i;
    }
    // Plain notes / description (fallback for both company and contact)
    else if (/^(note|notes|comment|comments|description|descriptions)$/.test(col)) map.notes = i;
    // Payment Terms — "Payment Terms", "Payment Term", "Pmt Terms", "Pmt Term"
    else if (/(payment|pmt)\s*terms?/.test(col)) {
      if (!map.paymentTerms) map.paymentTerms = i;
    }
    // Rating — flexible: "Rating", "Ratings", "Star Rating", "Rating (1-5)", etc.
    else if (/rating/.test(col)) {
      if (!map.rating) map.rating = i;
    }
    // Specialism
    else if (/^(specialism|specialisms|specialization|specialisation|specialty|speciality)$/.test(col)) map.specialism = i;
    // Prefix (e.g. "Mr.", "Dr.")
    else if (/^prefix$/.test(col)) map.prefix = i;
    // Address fields — Apple Contacts structured format "Address : Work : City" etc.
    // After normalization colons/punctuation are removed; use .*? so any number of
    // intervening words (work, home, other, …) are tolerated.
    else if (/\baddress\b.*\bcity\b/.test(col)) { if (!map.city) map.city = i; }
    else if (/\baddress\b.*\bstate\b/.test(col) || /\baddress\b.*\bprovince\b/.test(col)) { if (!map.state) map.state = i; }
    else if (/\baddress\b.*\bcountry\b/.test(col)) { if (!map.country) map.country = i; }
    else if (/\baddress\b.*\b(postcode|zip|postal|pincode)\b/.test(col) || /\baddress\b.*\bpin\s+code\b/.test(col)) { if (!map.postcode) map.postcode = i; }
    else if (/\baddress\b.*\b(full address|street)\b/.test(col) || /\baddress\b.*(full)\b.*\baddress\b/.test(col)) { if (!map.street) map.street = i; }
    // Generic address fields
    else if (/\bstreet\b/.test(col)) { if (!map.street) map.street = i; }
    else if (/^(city|location)$/.test(col) || /\bcity\b/.test(col)) { if (!map.city) map.city = i; }
    else if (/\bstate\b/.test(col) || /\bprovince\b/.test(col)) { if (!map.state) map.state = i; }
    else if (/\bcountry\b/.test(col)) { if (!map.country) map.country = i; }
    else if (/\bpost\s?code\b/.test(col) || /\bzip\b/.test(col) || /\bpostal\b/.test(col) || /\bpincode\b/.test(col) || /\bpin\s+code\b/.test(col)) { if (!map.postcode) map.postcode = i; }
    else if (/^(location)$/.test(col)) { if (!map.city) map.city = i; }
    else if (/^address$/.test(col)) { if (!map.street) map.street = i; }
  });

  // Need at least a name field to consider this valid
  const hasName = map.firstName !== undefined || map.lastName !== undefined || map.displayName !== undefined;
  return hasName ? map : null;
}

/**
 * Parse a CSV text into contact rows using detected headers.
 * Returns { rows, errors, headerMap }
 */
function parseContactsCSV(text, existingVendors) {
  if (!text.trim()) return { rows: [], errors: ['No data to parse.'], headerMap: null };

  const delimiter = detectDelimiter(text);
  // Use the full document parser so multiline quoted fields (e.g. bullet-point
  // notes) are kept as one field instead of being split into separate rows.
  const allRows = parseCSVDocument(text, delimiter);
  if (allRows.length < 2) return { rows: [], errors: ['Need at least a header row and one data row.'], headerMap: null };

  const headers = allRows[0];
  const headerMap = detectAppleContactsHeaders(headers);

  if (!headerMap) {
    return { rows: [], errors: ['Could not detect contact columns. Expected headers like: First, Last, Company, Phone, Email, Title, etc.'], headerMap: null };
  }

  const rows = [];
  const errors = [];

  for (let i = 1; i < allRows.length; i++) {
    const fields = allRows[i];
    if (fields.every(f => !f)) continue; // skip empty rows

    const get = (key) => (headerMap[key] !== undefined && fields[headerMap[key]]) ? fields[headerMap[key]].trim() : '';

    const prefix = get('prefix');
    const firstName = get('firstName');
    const lastName = get('lastName');
    const middleName = get('middleName');
    const displayName = get('displayName');
    const name = displayName || [prefix, firstName, middleName, lastName].filter(Boolean).join(' ') || '';
    const company = get('company');

    if (!name && !company) {
      errors.push(`Row ${i + 1}: no name or company found, skipped.`);
      continue;
    }

    // Try to match company to existing vendor (fuzzy matching)
    let matchedVendorIdx = -1;
    let matchType = 'new'; // 'existing' | 'new' | 'auto'
    let matchLevel = 'none'; // 'exact' | 'close' | 'fuzzy' | 'none'

    if (company.trim()) {
      const matches = findMatchingVendors(company, existingVendors || [], { limit: 1, minScore: 60 });
      if (matches.length > 0) {
        const best = matches[0];
        matchedVendorIdx = (existingVendors || []).findIndex(v => v.id === best.vendor.id);
        matchLevel = best.level;
        // exact/close auto-link; fuzzy shows suggestion but defaults to new
        matchType = (best.level === 'exact' || best.level === 'close') ? 'existing' : 'new';
      }
    } else {
      // No company — will auto-create using person's name
      matchType = 'auto';
    }

    // Contact person email: work/primary email, fall back to email2 (other) if empty
    const primaryEmail = get('email') || get('email2');
    // Company email: explicit "Email: Company" / "company email" column
    const companyEmail = get('companyEmail');
    // Company phone: explicit "Phone: Company" / "Company phone" column
    const companyPhone = get('companyPhone');
    // Company instagram and dropbox
    const companyInstagram = get('companyInstagram');
    const dropboxUrl = get('dropboxUrl');
    // Worked-with, BRIO Project and Team (raw; fuzzy-matched at import time)
    const workedWithBefore = get('workedWithBefore');
    const brioProjectRaw = get('brioProject');
    const brioTeamRaw = get('brioTeamInvolved');
    // Category, Budget, Last Engaged (raw; resolved at import time)
    const categoryRaw = get('category');
    const budgetRaw = get('budget');
    const lastEngaged = parseLastEngaged(get('lastEngaged'));
    // Contact-person specific notes (overrides plain notes for contact record)
    const contactNotes = get('contactNotes');
    // Company-specific notes (overrides plain notes for vendor record)
    const companyNotes = get('companyNotes');
    // Payment terms (vendor-level; supports multiline / bullet points via quoted CSV cell)
    const paymentTerms = get('paymentTerms');
    // Rating parsed to 0-5 integer
    const rating = parseRating(get('rating'));

    // Build location string from structured address fields for backwards compatibility
    const street = get('street');
    let city = get('city');
    const state = get('state');
    let addrCountry = get('country');
    const postcode = get('postcode');

    // Enrich country + city from phone number if not already set
    const phoneForLookup = get('phone') || get('altPhone');
    if (!addrCountry && phoneForLookup) {
      const phoneCountry = getCountryFromPhone(phoneForLookup);
      if (phoneCountry) addrCountry = phoneCountry;
    }
    if (!city && phoneForLookup) {
      const landlineInfo = getCityFromLandline(phoneForLookup);
      if (landlineInfo) {
        city = landlineInfo.city;
        if (!addrCountry) addrCountry = landlineInfo.country;
      }
    }

    const location = [city, addrCountry].filter(Boolean).join(', ');

    rows.push({
      _rowIdx: i,
      name,
      company: company || name, // fallback company = person name
      designation: get('designation'),
      phone: get('phone'),
      altPhone: get('altPhone'),
      fax: get('fax'),
      email: primaryEmail,
      companyEmail,
      companyPhone,
      companyInstagram,
      dropboxUrl,
      workedWithBefore,
      brioProjectRaw,
      brioTeamRaw,
      categoryRaw,
      budgetRaw,
      lastEngaged,
      contactNotes,
      companyNotes,
      paymentTerms,
      rating,
      linkedin: get('linkedin'),
      instagram: get('instagram'),
      website: cleanWebsite(get('website')),
      notes: get('notes'),
      specialism: get('specialism'),
      location,
      street,
      city,
      state,
      country: addrCountry,
      postcode,
      // Matching
      _matchType: matchType,
      _matchLevel: matchLevel,
      _matchedVendorId: matchedVendorIdx >= 0 ? (existingVendors || [])[matchedVendorIdx]?.id : null,
      _selectedVendorId: (matchLevel === 'exact' || matchLevel === 'close') && matchedVendorIdx >= 0
        ? (existingVendors || [])[matchedVendorIdx]?.id
        : '__new__',
      _newCompanyName: company || name,
    });
  }

  return { rows, errors, headerMap };
}

// ══════════════════════════════════════════════════════════════════════════
// IMPORT CONTACTS MODAL
// ══════════════════════════════════════════════════════════════════════════

/**
 * Try to guess a specialism from the contact's designation, name, or company name
 * by keyword-matching against the available specialism options.
 */
function guessSpecialism(row, specialismOptions) {
  if (!specialismOptions || specialismOptions.length === 0) return '';
  // Build a searchable text from available fields
  const text = [row.designation, row.name, row.company, row.notes].filter(Boolean).join(' ').toLowerCase();
  if (!text.trim()) return '';

  // Score each option: exact word match > partial match
  let bestMatch = '';
  let bestScore = 0;
  for (const opt of specialismOptions) {
    const label = (typeof opt === 'string' ? opt : opt.label || '').toLowerCase();
    if (!label) continue;
    // Split specialism label into keywords (skip very short words)
    const keywords = label.split(/[\s/&,()]+/).filter(w => w.length > 2);
    let score = 0;
    for (const kw of keywords) {
      if (text.includes(kw)) score += kw.length;
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = typeof opt === 'string' ? opt : opt.label;
    }
  }
  // Require a minimum match quality (at least one meaningful keyword match)
  return bestScore >= 4 ? bestMatch : '';
}

const COMPARE_FIELDS = [
  { key: 'designation', label: 'Title / Role' },
  { key: 'phone',       label: 'Phone' },
  { key: 'altPhone',    label: 'Alt Phone' },
  { key: 'email',       label: 'Email' },
  { key: 'linkedin',    label: 'LinkedIn' },
  { key: 'instagram',   label: 'Instagram' },
  { key: 'notes',       label: 'Notes', mergeable: true },
];

// Default field selection when a duplicate is first detected.
// Notes default to 'both' (merge) since you rarely want to discard either.
// All other fields default to 'incoming' when both sides differ.
function defaultFieldSel(existingVal, incomingVal, mergeable) {
  const ev = (existingVal || '').trim();
  const iv = (incomingVal || '').trim();
  if (!ev && !iv) return 'existing';
  if (ev && !iv)  return 'existing';
  if (!ev && iv)  return 'incoming';
  if (ev === iv)  return 'existing';
  return mergeable ? 'both' : 'incoming';
}

function initFieldSelections(dup, row) {
  const sels = {};
  for (const f of COMPARE_FIELDS) {
    const ev = dup[f.key] || '';
    const iv = f.key === 'notes' ? (row.contactNotes || row.notes || '') : (row[f.key] || '');
    sels[f.key] = defaultFieldSel(ev, iv, f.mergeable);
  }
  return sels;
}

function ImportContactsModal({ vendors, vendorContacts, onClose, onImport, specialismOptions, categoryOptions, projectOptions, userOptions }) {
  const [step, setStep] = useState('paste'); // 'paste' | 'preview'
  const [csvText, setCsvText] = useState('');
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [batchCategory, setBatchCategory] = useState('');
  const [batchSpecialism, setBatchSpecialism] = useState('');
  const [compareOpen, setCompareOpen] = useState(new Set());
  const [editMode, setEditMode] = useState(new Set());

  const toggleCompare = (idx) => setCompareOpen(prev => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });

  const toggleEdit = (idx) => setEditMode(prev => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });

  const batchSpecialismOptions = useMemo(() => {
    if (!specialismOptions || !specialismOptions.length) return [];
    if (!batchCategory) return [...new Set(specialismOptions.map(o => typeof o === 'string' ? o : o.label))].sort();
    return specialismOptions.filter(o => typeof o !== 'string' && o.category === batchCategory).map(o => o.label).sort();
  }, [specialismOptions, batchCategory]);

  const handleParse = () => {
    const result = parseContactsCSV(csvText, vendors);
    const cats = categoryOptions || CATEGORY_OPTIONS;
    // Enrich rows with resolved category, specialism suggestion, and duplicate detection
    const enriched = result.rows.map(row => {
      const dup = findDuplicateContact(row, vendorContacts);
      // For category: always pick the best-scoring option above a low floor (25),
      // since there are only 4 fixed choices and a partial match is almost certainly right.
      let resolvedCategory = '';
      if (row.categoryRaw) {
        const matched = fuzzyMatchMultiple(row.categoryRaw, cats);
        resolvedCategory = matched.split(',')[0]?.trim() || '';
        // If standard threshold missed, try best-above-25 fallback
        if (!resolvedCategory) {
          const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
          const compact = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
          const inp = norm(row.categoryRaw), inpC = compact(row.categoryRaw);
          let best = null, bestScore = 0;
          for (const opt of cats) {
            const optN = norm(opt), optC = compact(opt);
            let score = 0;
            if (optC === inpC || optN === inp) score = 100;
            else if (optC.includes(inpC) || inpC.includes(optC)) score = Math.round(85 * Math.min(optC.length, inpC.length) / Math.max(optC.length, inpC.length));
            else if (optN.includes(inp) || inp.includes(optN)) score = Math.round(75 * Math.min(optN.length, inp.length) / Math.max(optN.length, inp.length));
            const iw = inp.split(/\s+/).filter(w => w.length > 1);
            const ow = optN.split(/\s+/).filter(w => w.length > 1);
            const hits = iw.filter(w => ow.some(o => o.startsWith(w) || w.startsWith(o))).length;
            if (hits > 0) score = Math.max(score, Math.round(60 * hits / iw.length));
            if (score > bestScore) { bestScore = score; best = opt; }
          }
          if (best && bestScore >= 25) resolvedCategory = best;
        }
      }
      const resolvedBrioProject = row.brioProjectRaw
        ? fuzzyMatchMultiple(row.brioProjectRaw, projectOptions || [])
        : '';
      const resolvedBrioTeam = row.brioTeamRaw
        ? fuzzyMatchMultiple(row.brioTeamRaw, userOptions || [])
        : '';
      return {
        ...row,
        _resolvedCategory: resolvedCategory,
        _resolvedBrioProject: resolvedBrioProject,
        _resolvedBrioTeam: resolvedBrioTeam,
        _suggestedSpecialism: row.specialism || guessSpecialism(row, specialismOptions || []),
        _duplicateContact: dup || null,
        _duplicateAction: dup ? 'overwrite' : null,
        _fieldSelections: dup ? initFieldSelections(dup, row) : {},
        _fieldEdits: {},
      };
    });
    setRows(enriched);
    setErrors(result.errors);
    // Auto-set batch category selector to the most common per-row resolved category
    const catCounts = {};
    enriched.forEach(r => { if (r._resolvedCategory) catCounts[r._resolvedCategory] = (catCounts[r._resolvedCategory] || 0) + 1; });
    const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    if (topCat) setBatchCategory(topCat);
    if (enriched.length > 0) setStep('preview');
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      await onImport(rows, { category: batchCategory, specialism: batchSpecialism });
      onClose();
    } catch (e) {
      console.error('Import failed:', e);
    } finally {
      setImporting(false);
    }
  };

  const vendorOptions = useMemo(() => {
    const opts = (vendors || []).map(v => ({ id: v.id, label: v.companyName }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [vendors]);

  // Stats
  const newCompanies = useMemo(() => {
    const names = new Set();
    rows.forEach(r => { if (r._selectedVendorId === '__new__') names.add(r._newCompanyName); });
    return names.size;
  }, [rows]);

  const existingLinks = rows.filter(r => r._selectedVendorId !== '__new__').length;
  const suggestedMatches = rows.filter(r => r._selectedVendorId === '__new__' && r._matchedVendorId).length;
  const duplicateUpdates = rows.filter(r => r._duplicateContact && r._duplicateAction === 'overwrite').length;
  const activeImports = rows.length;

  return (
    <BrioModal title="Import Contacts" onClose={onClose} width="xl">
      {step === 'paste' && (
        <div>
          <p style={{ fontSize: '11px', color: '#666', marginBottom: 8, fontFamily: FONT }}>
            Paste a CSV export from Apple Contacts, Google Contacts, Outlook, or any spreadsheet.
            The system auto-detects columns like First, Last, Company, Phone, Email, Title, etc.
          </p>
          <textarea
            autoFocus
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
            placeholder={'First,Last,Company,Title,Phone 1 - Value,Phone 2 - Value,E-mail 1 - Value,URL,Note\nJohn,Smith,Acme Corp,Director,+31612345678,,john@acme.com,www.acme.com,Great partner'}
            rows={10}
            style={{
              width: '100%', fontSize: '11px', fontFamily: 'monospace',
              padding: '8px', border: '1px solid ' + BRIO_BORDER,
              borderRadius: 4, resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          {errors.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '6px 10px', marginTop: 8 }}>
              {errors.map((e, i) => <div key={i} style={{ fontSize: '10px', color: '#991b1b' }}>{e}</div>)}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={onClose} style={{ fontSize: '11px', padding: '5px 14px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
            <button
              onClick={handleParse}
              disabled={!csvText.trim()}
              style={{ fontSize: '11px', padding: '5px 14px', background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: FONT, opacity: csvText.trim() ? 1 : 0.5 }}
            >Preview</button>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div>
          {/* Summary */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: '11px', fontFamily: FONT }}>
            <span><strong>{rows.length}</strong> contact{rows.length !== 1 ? 's' : ''}</span>
            <span style={{ color: BRIO_GREEN }}><strong>{existingLinks}</strong> matched to existing</span>
            {suggestedMatches > 0 && (
              <span style={{ color: BRIO_GOLD }}><strong>{suggestedMatches}</strong> suggested match{suggestedMatches !== 1 ? 'es' : ''}</span>
            )}
            <span style={{ color: '#b45309' }}><strong>{newCompanies}</strong> new compan{newCompanies !== 1 ? 'ies' : 'y'}</span>
          </div>

          {/* Batch category & specialism selectors */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10, fontSize: '11px', fontFamily: FONT, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>Category for all:</label>
              <select
                value={batchCategory}
                onChange={e => { setBatchCategory(e.target.value); setBatchSpecialism(''); }}
                style={{ fontSize: '11px', padding: '3px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT }}
              >
                <option value="">— None —</option>
                {(categoryOptions || CATEGORY_OPTIONS).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>Specialism for all:</label>
              <select
                value={batchSpecialism}
                onChange={e => setBatchSpecialism(e.target.value)}
                style={{ fontSize: '11px', padding: '3px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT }}
              >
                <option value="">— None —</option>
                {batchSpecialismOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {errors.length > 0 && (
            <div style={{ background: '#fffcf0', border: '1px solid ' + BRIO_GOLD, borderRadius: 4, padding: '6px 10px', marginBottom: 8 }}>
              {errors.slice(0, 3).map((e, i) => <div key={i} style={{ fontSize: '10px', color: '#78660b' }}>{e}</div>)}
              {errors.length > 3 && <div style={{ fontSize: '10px', color: '#78660b' }}>+{errors.length - 3} more</div>}
            </div>
          )}

          {/* Preview table */}
          <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid ' + BRIO_BORDER, borderRadius: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', fontFamily: FONT }}>
              <thead>
                <tr style={{ background: BRIO_BG, position: 'sticky', top: 0, zIndex: 1 }}>
                  {['Name', 'Designation', 'Phone', 'Email', 'Country / City', 'Category', 'BRIO Project', 'BRIO Team', 'Notes', 'Company / Link To', ''].map((h, i) => (
                    <th key={i} style={{ padding: '4px 6px', textAlign: 'left', borderBottom: '1px solid ' + BRIO_BORDER, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} style={{
                    background: row._selectedVendorId !== '__new__' ? '#f0faf4'
                      : (row._matchedVendorId && row._matchLevel === 'fuzzy') ? '#fff7ed'
                      : '#fffcf0',
                    borderBottom: '1px solid #eee',
                  }}>
                    <td style={{ padding: '4px 6px', fontWeight: 500 }}>{row.name || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{row.designation || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{row.phone || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{row.email || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>
                      <span style={{ fontSize: '9px', color: (row.city || row.state || row.country) ? '#1e293b' : '#999' }}>
                        {[row.city, row.state, row.country].filter(Boolean).join(', ') || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '4px 4px', minWidth: 110 }}>
                      <select
                        value={row._resolvedCategory || batchCategory || ''}
                        onChange={e => updateRow(idx, '_resolvedCategory', e.target.value)}
                        title={row.categoryRaw ? `CSV value: "${row.categoryRaw}"` : ''}
                        style={{ fontSize: '9px', padding: '2px 3px', width: '100%', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, background: row._resolvedCategory ? '#f0faf4' : '#fff' }}
                      >
                        <option value="">— None —</option>
                        {(categoryOptions || CATEGORY_OPTIONS).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {row.categoryRaw && !row._resolvedCategory && (
                        <div style={{ fontSize: '8px', color: '#b45309', marginTop: 1 }} title="No matching category found">
                          "{row.categoryRaw}" — no match
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '4px 4px', minWidth: 120 }}>
                      <MultiSelectDropdown
                        value={row._resolvedBrioProject || ''}
                        options={projectOptions || []}
                        onChange={v => updateRow(idx, '_resolvedBrioProject', v)}
                        placeholder="—"
                      />
                      {row.brioProjectRaw && !row._resolvedBrioProject && (
                        <div style={{ fontSize: '8px', color: '#b45309', marginTop: 1 }} title="No matching project found">
                          "{row.brioProjectRaw}" — no match
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '4px 4px', minWidth: 110 }}>
                      <MultiSelectDropdown
                        value={row._resolvedBrioTeam || ''}
                        options={userOptions || []}
                        onChange={v => updateRow(idx, '_resolvedBrioTeam', v)}
                        placeholder="—"
                      />
                      {row.brioTeamRaw && !row._resolvedBrioTeam && (
                        <div style={{ fontSize: '8px', color: '#b45309', marginTop: 1 }} title="No matching team member found">
                          "{row.brioTeamRaw}" — no match
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '4px 6px', maxWidth: 160 }}>
                      <span style={{ fontSize: '9px', color: row.notes ? '#1e293b' : '#999', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {row.notes || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '4px 6px', minWidth: 180 }}>
                      <select
                        value={row._selectedVendorId || '__new__'}
                        onChange={e => {
                          const val = e.target.value;
                          setRows(prev => prev.map((r, i) => {
                            if (i !== idx) return r;
                            const updated = { ...r, _selectedVendorId: val };
                            if (val !== '__new__') {
                              const v = (vendors || []).find(v2 => String(v2.id) === val);
                              if (v) updated._newCompanyName = v.companyName;
                            }
                            const dup = findDuplicateContact(updated, vendorContacts);
                            updated._duplicateContact = dup || null;
                            updated._duplicateAction = dup ? 'overwrite' : null;
                            updated._fieldSelections = dup ? initFieldSelections(dup, updated) : {};
                            updated._fieldEdits = {};
                            return updated;
                          }));
                        }}
                        style={{ fontSize: '10px', padding: '2px 4px', width: '100%', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT }}
                      >
                        <option value="__new__">
                          + Create: {row._newCompanyName || row.company || 'Unknown'}
                        </option>
                        {vendorOptions.map(v => (
                          <option key={v.id} value={v.id}>
                            {v.label}
                            {row._matchedVendorId === v.id ? ' (suggested)' : ''}
                          </option>
                        ))}
                      </select>
                      {row._matchedVendorId && row._selectedVendorId === '__new__' && (
                        <div style={{ fontSize: '9px', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: 8, fontSize: '9px', fontWeight: 500,
                            background: row._matchLevel === 'close' ? 'rgba(159,144,42,0.12)' : 'rgba(180,83,9,0.08)',
                            color: row._matchLevel === 'close' ? BRIO_GOLD : '#b45309',
                          }}>
                            {row._matchLevel === 'close' ? 'Likely match' : 'Possible match'}
                          </span>
                          <span style={{ color: '#666' }}>
                            "{(vendors || []).find(v2 => v2.id === row._matchedVendorId)?.companyName}"
                          </span>
                          <button
                            onClick={() => updateRow(idx, '_selectedVendorId', row._matchedVendorId)}
                            style={{ background: 'none', border: 'none', color: BRIO_GOLD, cursor: 'pointer', fontSize: '9px', textDecoration: 'underline' }}
                          >Use this</button>
                        </div>
                      )}
                      {row._duplicateContact && (() => {
                        const dup = row._duplicateContact;
                        const isOpen = compareOpen.has(idx);
                        const isEditing = editMode.has(idx);
                        const sels = row._fieldSelections || {};
                        const edits = row._fieldEdits || {};

                        const getVal = (f, side) => {
                          if (edits[f.key]?.[side] !== undefined) return edits[f.key][side];
                          if (side === 'existing') return dup[f.key] || '';
                          return f.key === 'notes' ? (row.contactNotes || row.notes || '') : (row[f.key] || '');
                        };

                        const setEdit = (fKey, side, val) => {
                          updateRow(idx, '_fieldEdits', {
                            ...edits,
                            [fKey]: { ...(edits[fKey] || {}), [side]: val },
                          });
                        };

                        const toggleSel = (fKey, side) => {
                          const ev = getVal({ key: fKey }, 'existing');
                          const iv = getVal({ key: fKey }, 'incoming');
                          const f = COMPARE_FIELDS.find(x => x.key === fKey);
                          const current = sels[fKey] || defaultFieldSel(ev, iv, f?.mergeable);
                          let next;
                          if (side === 'existing') {
                            next = current === 'existing' ? 'incoming' : current === 'both' ? 'incoming' : 'both';
                          } else {
                            next = current === 'incoming' ? 'existing' : current === 'both' ? 'existing' : 'both';
                          }
                          updateRow(idx, '_fieldSelections', { ...sels, [fKey]: next });
                        };

                        // Only show fields where at least one side has content
                        const activeFields = COMPARE_FIELDS.filter(f => getVal(f, 'existing') || getVal(f, 'incoming'));

                        return (
                          <div style={{ marginTop: 4, padding: '4px 6px', background: '#fff7ed', border: '1px solid #f0c040', borderRadius: 3, fontSize: '9px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                              <span style={{ fontWeight: 600, color: '#92400e' }}>
                                Contact already exists
                                {!isOpen && <span style={{ fontWeight: 400, color: '#78350f', marginLeft: 4 }}>
                                  — {dup.designation || dup.email || dup.phone || ''}
                                </span>}
                              </span>
                              <div style={{ display: 'flex', gap: 4 }}>
                                {isOpen && (
                                  <button
                                    onClick={() => toggleEdit(idx)}
                                    style={{ background: isEditing ? '#92400e' : 'none', color: isEditing ? '#fff' : '#92400e', border: '1px solid #d97706', borderRadius: 2, cursor: 'pointer', fontSize: '8px', padding: '1px 5px', fontFamily: FONT }}
                                  >
                                    {isEditing ? '✓ Done editing' : 'Edit'}
                                  </button>
                                )}
                                <button
                                  onClick={() => toggleCompare(idx)}
                                  style={{ background: 'none', border: '1px solid #d97706', borderRadius: 2, color: '#92400e', cursor: 'pointer', fontSize: '8px', padding: '1px 5px', fontFamily: FONT }}
                                >
                                  {isOpen ? '▲ Collapse' : 'Compare & Resolve ▼'}
                                </button>
                              </div>
                            </div>

                            {isOpen && (
                              <>
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4, fontSize: '8.5px', tableLayout: 'fixed' }}>
                                  <colgroup>
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '41%' }} />
                                    <col style={{ width: '41%' }} />
                                  </colgroup>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #f0c040' }}>
                                      <th style={{ padding: '2px 3px', color: '#78350f', fontWeight: 600, textAlign: 'left' }}>Field</th>
                                      <th style={{ padding: '2px 3px', color: '#78350f', fontWeight: 600, textAlign: 'left' }}>Existing</th>
                                      <th style={{ padding: '2px 3px', color: '#78350f', fontWeight: 600, textAlign: 'left' }}>Incoming (CSV)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {activeFields.map(f => {
                                      const ev = getVal(f, 'existing');
                                      const iv = getVal(f, 'incoming');
                                      const sel = sels[f.key] || defaultFieldSel(ev, iv, f.mergeable);
                                      const useExisting = sel === 'existing' || sel === 'both';
                                      const useIncoming = sel === 'incoming' || sel === 'both';
                                      const differs = ev !== iv && ev && iv;
                                      const isMultiline = f.key === 'notes';
                                      return (
                                        <tr key={f.key} style={{ background: differs ? 'rgba(251,191,36,0.12)' : 'transparent', borderBottom: '1px solid #fde68a' }}>
                                          <td style={{ padding: '2px 3px', color: '#92400e', fontWeight: 500, verticalAlign: 'top', paddingTop: 4 }}>{f.label}</td>
                                          {/* Existing value */}
                                          <td style={{ padding: '2px 3px', verticalAlign: 'top' }}>
                                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 3, cursor: 'pointer' }}>
                                              <input
                                                type="checkbox"
                                                checked={useExisting}
                                                onChange={() => toggleSel(f.key, 'existing')}
                                                style={{ marginTop: 1, flexShrink: 0 }}
                                              />
                                              {isEditing ? (
                                                isMultiline
                                                  ? <textarea value={ev} onChange={e => setEdit(f.key, 'existing', e.target.value)}
                                                      style={{ fontSize: '8px', width: '100%', minHeight: 40, border: '1px solid #d97706', borderRadius: 2, fontFamily: FONT, padding: '1px 3px', resize: 'vertical' }} />
                                                  : <input value={ev} onChange={e => setEdit(f.key, 'existing', e.target.value)}
                                                      style={{ fontSize: '8px', width: '100%', border: '1px solid #d97706', borderRadius: 2, fontFamily: FONT, padding: '1px 3px' }} />
                                              ) : (
                                                <span style={{ color: ev ? (useExisting ? '#374151' : '#9ca3af') : '#9ca3af', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                  {ev || '—'}
                                                </span>
                                              )}
                                            </label>
                                          </td>
                                          {/* Incoming value */}
                                          <td style={{ padding: '2px 3px', verticalAlign: 'top' }}>
                                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 3, cursor: 'pointer' }}>
                                              <input
                                                type="checkbox"
                                                checked={useIncoming}
                                                onChange={() => toggleSel(f.key, 'incoming')}
                                                style={{ marginTop: 1, flexShrink: 0 }}
                                              />
                                              {isEditing ? (
                                                isMultiline
                                                  ? <textarea value={iv} onChange={e => setEdit(f.key, 'incoming', e.target.value)}
                                                      style={{ fontSize: '8px', width: '100%', minHeight: 40, border: '1px solid #d97706', borderRadius: 2, fontFamily: FONT, padding: '1px 3px', resize: 'vertical' }} />
                                                  : <input value={iv} onChange={e => setEdit(f.key, 'incoming', e.target.value)}
                                                      style={{ fontSize: '8px', width: '100%', border: '1px solid #d97706', borderRadius: 2, fontFamily: FONT, padding: '1px 3px' }} />
                                              ) : (
                                                <span style={{ color: iv ? (useIncoming ? '#374151' : '#9ca3af') : '#9ca3af', fontWeight: differs && useIncoming ? 600 : 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                  {iv || '—'}
                                                </span>
                                              )}
                                            </label>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {activeFields.some(f => (sels[f.key] || defaultFieldSel(getVal(f,'existing'), getVal(f,'incoming'), f.mergeable)) === 'both') && (
                                  <div style={{ fontSize: '8px', color: '#b45309', marginBottom: 3 }}>
                                    ✦ Fields with both selected will be merged
                                  </div>
                                )}
                                <button
                                  onClick={() => updateRow(idx, '_duplicateAction', 'add')}
                                  style={{ background: 'none', border: '1px solid #d97706', borderRadius: 2, color: '#92400e', cursor: 'pointer', fontSize: '8px', padding: '1px 6px', fontFamily: FONT }}
                                >
                                  + Add as new contact instead
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center', verticalAlign: 'top' }}>
                      <button onClick={() => removeRow(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_RED, padding: 2 }}>
                        <X size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button onClick={() => setStep('paste')} style={{ fontSize: '11px', padding: '5px 14px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer', fontFamily: FONT }}>
              Back
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{ fontSize: '11px', padding: '5px 14px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
              <button
                onClick={handleImport}
                disabled={importing || activeImports === 0}
                style={{ fontSize: '11px', padding: '5px 14px', background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: FONT, opacity: (importing || activeImports === 0) ? 0.5 : 1 }}
              >
                {importing ? 'Importing...' : (
                  duplicateUpdates > 0
                    ? `Import ${activeImports} contact${activeImports !== 1 ? 's' : ''} (${duplicateUpdates} update${duplicateUpdates !== 1 ? 's' : ''})`
                    : `Import ${activeImports} contact${activeImports !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </BrioModal>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ENRICHMENT REVIEW MODAL
// ══════════════════════════════════════════════════════════════════════════

const ENRICHABLE_FIELDS = [
  { key: 'phone',        label: 'Phone' },
  { key: 'website',      label: 'Website' },
  { key: 'street',       label: 'Street' },
  { key: 'city',         label: 'City' },
  { key: 'state',        label: 'State / Province' },
  { key: 'country',      label: 'Country' },
  { key: 'postcode',     label: 'Postcode' },
  { key: 'location',     label: 'Location' },
];

function EnrichmentReviewModal({ modal, onClose, onApply }) {
  const { vendorName, currentData, loading, error, results, source, googleKeyMissing } = modal;
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [fields, setFields] = useState({});
  const [edited, setEdited] = useState({});
  const [selectedEmail, setSelectedEmail] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState('');

  const result = results?.[selectedIdx] || null;

  // Initialize field checkboxes and edited values when result changes
  useEffect(() => {
    if (!result || !currentData) return;
    const f = {};
    const e = {};
    for (const { key } of ENRICHABLE_FIELDS) {
      e[key] = result[key] || '';
      // Pre-check fields that are currently empty on the vendor
      f[key] = !currentData[key] && !!result[key];
    }
    setFields(f);
    setEdited(e);
    setSelectedEmail('');
    setApplyError('');
  }, [result, currentData]);

  const toggleField = (key) => setFields(prev => ({ ...prev, [key]: !prev[key] }));
  const editField = (key, val) => setEdited(prev => ({ ...prev, [key]: val }));

  const checkedCount = Object.values(fields).filter(Boolean).length + (selectedEmail ? 1 : 0);

  const handleApply = async () => {
    setApplying(true);
    setApplyError('');
    const updates = {};
    for (const { key } of ENRICHABLE_FIELDS) {
      if (fields[key] && edited[key]) updates[key] = edited[key];
    }
    if (selectedEmail) updates.companyEmail = selectedEmail;
    updates.enrichmentSource = source || '';
    updates.enrichmentCheckedAt = new Date().toISOString();

    const res = await onApply(modal.vendorId, updates);
    setApplying(false);
    if (res?.success === false) {
      setApplyError(res.error || 'Failed to save');
    }
  };

  const SOURCE_LABEL = { google_places: 'Google Places', openstreetmap: 'OpenStreetMap', none: 'No source' };
  const SOURCE_COLOR = { google_places: BRIO_GREEN, openstreetmap: '#b45309' };

  return (
    <BrioModal title={`Auto-fill: ${vendorName}`} onClose={onClose} width="xl">
      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 40, fontFamily: FONT }}>
          <Loader2 size={18} className="animate-spin" style={{ color: BRIO_GOLD }} />
          <span style={{ fontSize: '13px', color: '#666' }}>Searching for company data...</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div style={{ padding: 20, fontFamily: FONT }}>
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', fontSize: '12px', color: '#991b1b', marginBottom: 12 }}>
            {error}
          </div>
          <button
            onClick={onClose}
            style={{ fontSize: '11px', padding: '5px 14px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer', fontFamily: FONT }}
          >Close</button>
        </div>
      )}

      {/* No results */}
      {!loading && !error && results && results.length === 0 && (
        <div style={{ padding: 20, fontFamily: FONT, fontSize: '12px', color: '#666', textAlign: 'center' }}>
          <p style={{ marginBottom: 8 }}>No results found for "{vendorName}".</p>
          <p style={{ fontSize: '11px', color: '#999' }}>Try editing the company name or adding a location.</p>
          <button
            onClick={onClose}
            style={{ marginTop: 12, fontSize: '11px', padding: '5px 14px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer', fontFamily: FONT }}
          >Close</button>
        </div>
      )}

      {/* Results */}
      {!loading && !error && result && (
        <div style={{ fontFamily: FONT }}>
          {/* Source badge + result tabs */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{
              display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: '10px', fontWeight: 600,
              background: (SOURCE_COLOR[source] || '#999') + '20', color: SOURCE_COLOR[source] || '#999',
            }}>
              {SOURCE_LABEL[source] || source}
            </span>
            {results.length > 1 && (
              <div style={{ display: 'flex', gap: 4 }}>
                {results.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedIdx(i)}
                    style={{
                      padding: '2px 10px', fontSize: '10px', borderRadius: 3, cursor: 'pointer', fontFamily: FONT,
                      border: '1px solid ' + (i === selectedIdx ? BRIO_GOLD : BRIO_BORDER),
                      background: i === selectedIdx ? BRIO_GOLD + '15' : 'transparent',
                      color: i === selectedIdx ? BRIO_GOLD : '#666', fontWeight: i === selectedIdx ? 600 : 400,
                    }}
                  >
                    {r.name || `Result ${i + 1}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Found name */}
          {result.name && (
            <div style={{ fontSize: '11px', color: '#666', marginBottom: 8 }}>
              Found: <strong style={{ color: '#1e293b' }}>{result.name}</strong>
            </div>
          )}

          {/* Google Places API key warning */}
          {googleKeyMissing && (
            <div style={{
              background: '#fffcf0', border: '1px solid ' + BRIO_GOLD, borderRadius: 4,
              padding: '6px 10px', marginBottom: 10, fontSize: '10px', color: '#78660b',
            }}>
              <strong>Limited results:</strong> Google Places API key is not configured.
              Only address data is available via OpenStreetMap (no phone or website).
              Add <code style={{ background: '#f0ede0', padding: '0 3px', borderRadius: 2 }}>GOOGLE_PLACES_API_KEY</code> in
              Vercel environment variables for full enrichment.
            </div>
          )}

          {/* Field comparison table */}
          <div style={{ border: '1px solid ' + BRIO_BORDER, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ background: BRIO_BG }}>
                  <th style={{ padding: '5px 6px', width: 30, textAlign: 'center', borderBottom: '1px solid ' + BRIO_BORDER }}></th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid ' + BRIO_BORDER, fontWeight: 600, color: '#555' }}>Field</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid ' + BRIO_BORDER, fontWeight: 600, color: '#555' }}>Current</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid ' + BRIO_BORDER, fontWeight: 600, color: '#555' }}>Suggested</th>
                </tr>
              </thead>
              <tbody>
                {ENRICHABLE_FIELDS.map(({ key, label }) => {
                  const current = currentData[key] || '';
                  const suggested = edited[key] || '';
                  const checked = fields[key];
                  const same = current && current === suggested;
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #f0f0f0', background: checked ? '#f0faf4' : 'transparent' }}>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={checked || false}
                          onChange={() => toggleField(key)}
                          disabled={!suggested}
                          style={{ cursor: suggested ? 'pointer' : 'default' }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>{label}</td>
                      <td style={{ padding: '4px 8px', color: current ? '#1e293b' : '#ccc' }}>
                        {current || '(empty)'}
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {same ? (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>Same as current</span>
                        ) : suggested ? (
                          <input
                            type="text"
                            value={suggested}
                            onChange={e => editField(key, e.target.value)}
                            style={{
                              width: '100%', boxSizing: 'border-box', fontSize: '11px', padding: '2px 4px',
                              border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, outline: 'none',
                              background: current && current !== suggested ? '#fffcf0' : 'transparent',
                            }}
                          />
                        ) : (
                          <span style={{ color: '#ccc' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Email suggestions */}
          {result.suggestedEmails && result.suggestedEmails.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: 4 }}>
                Suggested Emails <span style={{ fontWeight: 400, color: '#999', fontStyle: 'italic' }}>(unverified)</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {result.suggestedEmails.map(email => (
                  <label key={email} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '11px', cursor: 'pointer', color: '#374151' }}>
                    <input
                      type="radio"
                      name="enrichEmail"
                      checked={selectedEmail === email}
                      onChange={() => setSelectedEmail(email)}
                      style={{ cursor: 'pointer' }}
                    />
                    {email}
                  </label>
                ))}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '11px', cursor: 'pointer', color: '#999' }}>
                  <input
                    type="radio"
                    name="enrichEmail"
                    checked={!selectedEmail}
                    onChange={() => setSelectedEmail('')}
                    style={{ cursor: 'pointer' }}
                  />
                  Skip email
                </label>
              </div>
              {currentData.companyEmail && selectedEmail && (
                <div style={{ fontSize: '10px', color: '#b45309', marginTop: 4 }}>
                  Current email: {currentData.companyEmail} — will be replaced
                </div>
              )}
            </div>
          )}

          {/* Apply error */}
          {applyError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '6px 10px', fontSize: '10px', color: '#991b1b', marginBottom: 8 }}>
              {applyError}
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              onClick={onClose}
              style={{ fontSize: '11px', padding: '5px 14px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer', fontFamily: FONT }}
            >Cancel</button>
            <button
              onClick={handleApply}
              disabled={applying || checkedCount === 0}
              style={{
                fontSize: '11px', padding: '5px 14px', borderRadius: 4, cursor: applying ? 'wait' : 'pointer',
                fontFamily: FONT, border: 'none', fontWeight: 600,
                background: BRIO_GOLD, color: '#fff',
                opacity: (applying || checkedCount === 0) ? 0.5 : 1,
              }}
            >
              {applying ? 'Applying...' : `Apply ${checkedCount} field${checkedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
    </BrioModal>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// GIVE ACCESS MODAL — grant per-user access (Read only / Full access) to the
// Contacts page. Admin-only trigger; persists to app_settings.contactsAccess.
// Mirrors the Timelines page "Give access" affordance.
// ══════════════════════════════════════════════════════════════════════════

const isAdminRole = (role) => role === 'admin' || role === 'admin-pa';

// Effective level for a user given the stored grant map (kept in sync with
// getContactsAccessLevel() in App.js). Absent grant → role default.
function effectiveAccessLevel(user, accessMap) {
  // Inactive users have no access, regardless of role or explicit grant.
  if (user.isActive === false || user.status === 'inactive') return 'none';
  if (isAdminRole(user.role)) return 'full';
  const g = accessMap?.[String(user.id)];
  if (g === 'read' || g === 'full' || g === 'none') return g;
  return user.role === 'supervisor' ? 'full' : 'none';
}

function ContactsGiveAccessModal({ users, currentAccess, onSave, onClose }) {
  const sortedUsers = React.useMemo(
    () => [...users].sort((a, b) =>
      String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''))),
    [users]
  );
  const [levels, setLevels] = useState(() => {
    const init = {};
    users.forEach(u => { init[String(u.id)] = effectiveAccessLevel(u, currentAccess); });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const setLevel = (id, val) => setLevels(prev => ({ ...prev, [String(id)]: val }));

  const handleSave = async () => {
    // Start from the existing map so grants for users NOT shown in this list
    // (e.g. inactive users, who are filtered out) are preserved rather than
    // silently dropped. For each shown user, store their explicit selection only
    // when it differs from the role default (keeps the map minimal); admins are
    // never stored — they always keep full access.
    const map = { ...(currentAccess && typeof currentAccess === 'object' ? currentAccess : {}) };
    users.forEach(u => {
      const key = String(u.id);
      if (isAdminRole(u.role)) { delete map[key]; return; }
      const sel = levels[key] || 'none';
      const roleDefault = u.role === 'supervisor' ? 'full' : 'none';
      if (sel !== roleDefault) map[key] = sel;
      else delete map[key];
    });
    // The write can be rejected by RLS (app_settings 'contactsAccess' is only
    // writable by admins and by supervisors with effective 'full' access).
    // updateSetting() resolves with { success: false } rather than throwing, so
    // surface that here — closing the modal on a rejected write is what made a
    // permission error look like a selection that silently would not stick.
    setSaving(true);
    setSaveError(null);
    try {
      const res = await onSave(map);
      if (res && res.success === false) {
        setSaveError(res.error || 'Could not save access changes — you may not have permission.');
      }
    } catch (err) {
      setSaveError(err?.message || 'Could not save access changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 11000, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: '360px', maxWidth: '440px', width: '100%', padding: '20px 24px', fontFamily: FONT }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '4px' }}>
          <Users size={14} style={{ color: BRIO_GOLD }} />
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Give Access</div>
        </div>
        <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, marginBottom: '14px', lineHeight: '15px' }}>
          Choose who can open the Contacts page. <b>Read only</b> can view, expand
          and filter contacts but cannot make changes; <b>Full access</b> can also
          edit. Admins always have full access.
        </div>
        {sortedUsers.length === 0 ? (
          <div style={{ fontSize: '11px', fontWeight: 300, color: '#aaa', marginBottom: '18px' }}>No users available.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '320px', overflowY: 'auto', marginBottom: '18px' }}>
            {sortedUsers.map(u => {
              const locked = isAdminRole(u.role);
              const selected = locked ? 'full' : (levels[String(u.id)] || 'none');
              const tone = locked
                ? ACCESS_LEVEL_STYLE_LOCKED
                : (ACCESS_LEVEL_STYLES[selected] || ACCESS_LEVEL_STYLES.none);
              return (
                <div key={String(u.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 300, color: '#333' }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email || ''}>
                    {u.name || u.email || `User ${u.id}`}
                    {locked && <span style={{ fontSize: '9px', color: BRIO_GREY, marginLeft: 6 }}>admin</span>}
                  </span>
                  <select
                    value={selected}
                    disabled={locked}
                    onChange={e => setLevel(u.id, e.target.value)}
                    style={{ fontSize: '11px', padding: '3px 6px', border: '1px solid ' + tone.border, borderRadius: 3, fontFamily: FONT, background: tone.background, color: tone.color, minWidth: 112, cursor: locked ? 'default' : 'pointer' }}
                  >
                    {/* Options keep the plain menu palette — only the closed
                        control is tinted by the level. */}
                    {ACCESS_LEVEL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value} style={{ background: '#fff', color: '#333' }}>{o.label}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
        {saveError && (
          <div style={{ fontSize: '11px', fontWeight: 300, color: '#933', background: '#fdf3f3', border: '1px solid #e8c9c9', padding: '6px 8px', marginBottom: '10px', lineHeight: '15px' }}>
            {saveError}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '5px 14px', fontSize: '11px', fontFamily: FONT, fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: saving ? 'default' : 'pointer', color: '#555' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '5px 14px', fontSize: '11px', fontFamily: FONT, fontWeight: 400, border: '1px solid ' + BRIO_GOLD, background: BRIO_GOLD, color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export default function ContactsDirectoryPage({
  currentUser,
  isAdmin,
  activeProjects = [],
  activeUsers = [],
  vendorsHook,
  readOnly = false,
  canManageAccess = false,
  contactsAccess = {},
  onSaveContactsAccess,
}) {
  // ── State ──────────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [sortRules, setSortRules] = useState([{ key: 'companyName', dir: 'asc' }]);
  const [filterRules, setFilterRulesRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(CD_FILTER_RULES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const setFilterRules = useCallback((next) => {
    const val = typeof next === 'function' ? next(filterRules) : next;
    setFilterRulesRaw(val);
    try { localStorage.setItem(CD_FILTER_RULES_KEY, JSON.stringify(val)); } catch {}
  }, [filterRules]);
  const [activeCategoryTab, setActiveCategoryTabRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(CD_CATEGORY_TAB_KEY);
      return saved || null;
    } catch { return null; }
  });
  const setActiveCategoryTab = useCallback((tab) => {
    setActiveCategoryTabRaw(tab);
    try {
      if (tab) localStorage.setItem(CD_CATEGORY_TAB_KEY, tab);
      else localStorage.removeItem(CD_CATEGORY_TAB_KEY);
    } catch {}
  }, []);
  const [showSortPanel, setShowSortPanel] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [expandedId, setExpandedId] = useState(null); // drawer company id
  const [addingRow, setAddingRow] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showGiveAccessModal, setShowGiveAccessModal] = useState(false); // Give Access modal
  const [notesDrawer, setNotesDrawer] = useState(null); // { vendorId, name, notes }
  const [enrichmentModal, setEnrichmentModal] = useState(null);
  const [highlightedVendorId, setHighlightedVendorId] = useState(null);

  // ── Specialism options hook ─────────────────────────────────────────
  const specialismHook = useSpecialismOptions();

  // ── Column order / freeze / collapse ──────────────────────────────────
  const [frozenCols, setFrozenCols] = useState(() => loadFrozenCols());
  const toggleFreezeCol = useCallback((key) => {
    setFrozenCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(CD_FROZEN_COLS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const [columnOrder, setColumnOrder] = useState(() => loadStoredColumnOrder());
  const orderedColumns = useMemo(() => {
    const cols = getOrderedColumns(columnOrder, frozenCols);
    const isMediaTab = activeCategoryTab && String(activeCategoryTab).toLowerCase() === 'media';
    return cols.filter(c => !c.mediaOnly || isMediaTab);
  }, [columnOrder, frozenCols, activeCategoryTab]);

  const moveColumn = useCallback((fromIdx, toIdx) => {
    setColumnOrder(prev => {
      const currentKeys = getOrderedColumns(prev, frozenCols).map(c => c.key);
      const firstNonFrozen = currentKeys.findIndex(k => {
        const col = COLUMNS.find(c => c.key === k);
        return !col?.alwaysFrozen && !frozenCols[k];
      });
      if (toIdx < firstNonFrozen && toIdx < fromIdx) return prev;
      const next = [...currentKeys];
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      try { localStorage.setItem(CD_COL_ORDER_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [frozenCols]);

  const dragColIdxRef = useRef(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);

  const [collapsedCols, setCollapsedCols] = useState(() => {
    try {
      const saved = localStorage.getItem(CD_COLLAPSED_COLS_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const toggleColCollapse = useCallback((key) => {
    setCollapsedCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(CD_COLLAPSED_COLS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const { vendors = [], vendorContacts = [], loading, error: vendorsError,
    createVendor: _createVendor, updateVendor: _updateVendor, deleteVendor: _deleteVendor,
    createVendorContact: _createVendorContact, updateVendorContact: _updateVendorContact,
    deleteVendorContact: _deleteVendorContact } = vendorsHook || {};

  // Read-only viewers cannot mutate. Nulling out every write method turns each
  // handler's existing `if (!createVendor) return;` guard into a no-op, giving a
  // single choke point for write access behind the (also hidden) action buttons
  // and the inert EditableCell inputs. Read data (vendors, vendorContacts) is
  // untouched so the page still renders fully.
  const createVendor = readOnly ? undefined : _createVendor;
  const updateVendor = readOnly ? undefined : _updateVendor;
  const deleteVendor = readOnly ? undefined : _deleteVendor;
  const createVendorContact = readOnly ? undefined : _createVendorContact;
  const updateVendorContact = readOnly ? undefined : _updateVendorContact;
  const deleteVendorContact = readOnly ? undefined : _deleteVendorContact;

  // ── Helpers ────────────────────────────────────────────────────────────
  const projectMap = useMemo(() => {
    const m = {};
    (activeProjects || []).forEach(p => { m[p.id] = p.name || p.projectCode || `#${p.id}`; });
    return m;
  }, [activeProjects]);

  const userMap = useMemo(() => {
    const m = {};
    (activeUsers || []).forEach(u => { m[u.id] = u.name || u.email || `#${u.id}`; });
    return m;
  }, [activeUsers]);

  // ── Filter & sort ──────────────────────────────────────────────────────
  // Media is restricted by default; supervisors see it, and a user with an
  // active 'contacts-directory' temp grant gets supervisor-equivalent scope.
  const hasContactsDirectoryTempAccess = (() => {
    const pages = currentUser?.tempAccessPages;
    if (!Array.isArray(pages) || !pages.includes('contacts-directory')) return false;
    const expiresAt = currentUser?.tempAccessExpiresAt;
    if (!expiresAt) return false;
    const t = new Date(expiresAt).getTime();
    return Number.isFinite(t) && t > Date.now();
  })();
  const canSeeMedia = isAdmin || currentUser?.role === 'supervisor' || hasContactsDirectoryTempAccess;
  const visibleCategoryOptions = useMemo(() =>
    canSeeMedia ? CATEGORY_OPTIONS : CATEGORY_OPTIONS.filter(c => c !== 'Media'),
    [canSeeMedia]
  );

  // Persisted filter/tab state can target a category the current user can't
  // see (e.g. a 'Category is Media' rule left over from a prior session).
  // Once the page hides Media, that filter narrows the list to rows that are
  // then also stripped — net result: an unexplained empty table. Drop any
  // such filter so the user sees the rest of their data.
  useEffect(() => {
    if (canSeeMedia) return;
    const isMediaCategoryRule = (r) => r && r.key === 'category'
      && typeof r.value === 'string' && r.value.toLowerCase() === 'media';
    const hasStaleRule = filterRules.some(isMediaCategoryRule);
    if (hasStaleRule) setFilterRules(filterRules.filter(r => !isMediaCategoryRule(r)));
    if (activeCategoryTab && String(activeCategoryTab).toLowerCase() === 'media') {
      setActiveCategoryTab(null);
    }
  }, [canSeeMedia, filterRules, activeCategoryTab, setFilterRules, setActiveCategoryTab]);

  const filteredVendors = useMemo(() => {
    // Build a map of latest contact import date per vendor
    const latestContactDate = {};
    (vendorContacts || []).forEach(c => {
      if (c.createdAt && (!latestContactDate[c.vendorId] || c.createdAt > latestContactDate[c.vendorId])) {
        latestContactDate[c.vendorId] = c.createdAt;
      }
    });
    let list = (vendors || []).map(v => {
      const vendorDate = v.createdAt || '';
      const contactDate = latestContactDate[v.id] || '';
      const effectiveDate = contactDate > vendorDate ? contactDate : vendorDate;
      return {
        ...v,
        dateAdded: effectiveDate ? effectiveDate.slice(0, 10).replace(/-/g, '') : '',
      };
    });
    // Hide Media category from regular users
    if (!canSeeMedia) {
      list = list.filter(v => v.category !== 'Media');
    }
    // Apply category tab filter (skipped when a search is active so contacts
    // in other categories are still reachable by name/email search)
    if (activeCategoryTab && !searchText) {
      list = list.filter(v => v.category === activeCategoryTab);
    }
    if (searchText) {
      const q = searchText.toLowerCase();
      const contactsByVendor = {};
      (vendorContacts || []).forEach(c => {
        if (!contactsByVendor[c.vendorId]) contactsByVendor[c.vendorId] = [];
        contactsByVendor[c.vendorId].push(c);
      });
      list = list.filter(v => {
        // Search all vendor fields
        if ((v.companyName || '').toLowerCase().includes(q)) return true;
        if ((v.companyEmail || '').toLowerCase().includes(q)) return true;
        if ((v.website || '').toLowerCase().includes(q)) return true;
        if ((v.instagram || '').toLowerCase().includes(q)) return true;
        if ((v.budget || '').toLowerCase().includes(q)) return true;
        if ((v.category || '').toLowerCase().includes(q)) return true;
        if ((v.specialism || '').toLowerCase().includes(q)) return true;
        if ((v.workedWithBefore || '').toLowerCase().includes(q)) return true;
        if ((v.brioProject || '').toLowerCase().includes(q)) return true;
        if ((v.brioTeamInvolved || '').toLowerCase().includes(q)) return true;
        if ((v.location || '').toLowerCase().includes(q)) return true;
        if ((v.phone || '').toLowerCase().includes(q)) return true;
        if ((v.paymentTerms || '').toLowerCase().includes(q)) return true;
        if ((v.notes || '').toLowerCase().includes(q)) return true;
        // Search contact people
        const contacts = contactsByVendor[v.id] || [];
        return contacts.some(c =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.phone || '').toLowerCase().includes(q) ||
          (c.designation || '').toLowerCase().includes(q) ||
          (c.linkedin || '').toLowerCase().includes(q) ||
          (c.instagram || '').toLowerCase().includes(q) ||
          (c.notes || '').toLowerCase().includes(q)
        );
      });
    }
    // Apply multi-rule filters
    for (const rule of filterRules) {
      if (!rule.value) continue;
      list = list.filter(v => {
        const val = (v[rule.key] || '').toLowerCase();
        const target = rule.value.toLowerCase();
        if (rule.condition === 'is') return val === target;
        if (rule.condition === 'is not') return val !== target;
        if (rule.condition === 'contains') return val.includes(target);
        return true;
      });
    }
    // Apply multi-rule sort
    if (sortRules.length > 0) {
      list.sort((a, b) => {
        for (const rule of sortRules) {
          let aVal = a[rule.key] || '';
          let bVal = b[rule.key] || '';
          if (rule.key === 'rating') { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
          if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
          if (aVal < bVal) return rule.dir === 'asc' ? -1 : 1;
          if (aVal > bVal) return rule.dir === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return list;
  }, [vendors, vendorContacts, searchText, filterRules, sortRules, canSeeMedia, activeCategoryTab]);

  // ── CRUD handlers ──────────────────────────────────────────────────────
  const handleFieldSave = useCallback(async (vendorId, field, value) => {
    if (!updateVendor) return;
    const result = await updateVendor(vendorId, { [field]: value });
    if (!result?.success) console.error('Failed to save field:', result?.error);
  }, [updateVendor]);

  const handleLocationSave = useCallback(async (vendorId, fields) => {
    if (!updateVendor) return;
    await updateVendor(vendorId, fields);
  }, [updateVendor]);

  const handleAddCompany = useCallback(async () => {
    if (!createVendor || !newRow.companyName?.trim()) return;
    // Check for potential duplicates before creating
    const matches = findMatchingVendors(newRow.companyName, vendors, { limit: 3, minScore: 70 });
    if (matches.length > 0) {
      const names = matches.map(m => `"${m.vendor.companyName}"`).join(', ');
      if (!window.confirm(
        `Similar compan${matches.length > 1 ? 'ies' : 'y'} already exist${matches.length === 1 ? 's' : ''}: ${names}.\n\nCreate "${newRow.companyName.trim()}" anyway?`
      )) return;
    }
    const result = await createVendor(newRow);
    if (result?.success) {
      setNewRow({});
      setAddingRow(false);
    }
  }, [createVendor, newRow, vendors]);

  const handleDeleteCompany = useCallback(async (id) => {
    if (!deleteVendor) return;
    if (!window.confirm('Delete this company and all its contacts?')) return;
    await deleteVendor(id);
    if (expandedId === id) setExpandedId(null);
  }, [deleteVendor, expandedId]);

  // Contact people CRUD
  const handleAddContact = useCallback(async () => {
    if (!createVendorContact || !expandedId) return;
    await createVendorContact({ vendorId: expandedId, name: 'New Contact' });
  }, [createVendorContact, expandedId]);

  const handleUpdateContact = useCallback(async (contactId, field, value) => {
    if (!updateVendorContact) return;
    await updateVendorContact(contactId, { [field]: value });
  }, [updateVendorContact]);

  const handleDeleteContact = useCallback(async (contactId) => {
    if (!deleteVendorContact) return;
    if (!window.confirm('Remove this contact person?')) return;
    await deleteVendorContact(contactId);
  }, [deleteVendorContact]);

  // Move a contact to a different company
  const handleMoveContact = useCallback(async (contactId, newVendorId) => {
    if (!updateVendorContact || !newVendorId) return;
    await updateVendorContact(contactId, { vendorId: newVendorId });
  }, [updateVendorContact]);

  // Create a new company from a contact's details and move the contact to it
  const handleCreateAndMoveContact = useCallback(async (contact, companyName) => {
    if (!createVendor || !updateVendorContact) return;
    const website = cleanWebsite(websiteFromEmail(contact.email));
    const result = await createVendor({
      companyName,
      website,
      companyEmail: contact.email || '',
      phone: contact.phone || '',
    });
    if (result?.success && result.id) {
      await updateVendorContact(contact.id, { vendorId: result.id });
    }
  }, [createVendor, updateVendorContact]);

  // ── Project & user options for selects ─────────────────────────────────
  const projectOptions = useMemo(() =>
    [...(activeProjects || [])]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(p => p.name || `#${p.id}`),
    [activeProjects]
  );

  const userOptions = useMemo(() =>
    (activeUsers || []).map(u => u.name || u.email || `#${u.id}`),
    [activeUsers]
  );

  // Import contacts from CSV
  const handleImportContacts = useCallback(async (rows, options = {}) => {
    if (!createVendor || !createVendorContact) return;
    const { category = '', specialism = '' } = options;

    // Group rows by company target
    // 1. Create new companies first, collect their IDs
    const newCompanyNames = new Set();
    rows.forEach(r => { if (r._selectedVendorId === '__new__') newCompanyNames.add(r._newCompanyName); });

    const newCompanyIdMap = {}; // companyName (lowercase) → vendor id
    for (const name of newCompanyNames) {
      const sampleRow = rows.find(r => r._selectedVendorId === '__new__' && r._newCompanyName === name);
      // Derive website from contact emails if not already set
      let website = cleanWebsite(sampleRow?.website || '');
      if (!website) {
        const companyRows = rows.filter(r => r._selectedVendorId === '__new__' && r._newCompanyName === name);
        for (const cr of companyRows) {
          const derived = websiteFromEmail(cr.email);
          if (derived) { website = derived; break; }
        }
      }
      // Per-row category: use value resolved (and possibly overridden) in preview
      const rowCategory = sampleRow?._resolvedCategory || category;
      // Per-row budget: fuzzy-match to High / Mid / Low
      const rowBudget = sampleRow?.budgetRaw
        ? (fuzzyMatchMultiple(sampleRow.budgetRaw, ['High', 'Mid', 'Low']).split(',')[0]?.trim() || '')
        : '';
      const result = await createVendor({
        companyName: name,
        category: rowCategory,
        specialism: specialism || '',
        website,
        companyEmail: sampleRow?.companyEmail || '',
        phone: sampleRow?.companyPhone || '',
        instagram: sampleRow?.companyInstagram || '',
        dropboxUrl: sampleRow?.dropboxUrl || '',
        workedWithBefore: sampleRow?.workedWithBefore || '',
        brioProject: sampleRow?._resolvedBrioProject !== undefined
          ? sampleRow._resolvedBrioProject
          : fuzzyMatchMultiple(sampleRow?.brioProjectRaw || '', projectOptions),
        brioTeamInvolved: sampleRow?._resolvedBrioTeam !== undefined
          ? sampleRow._resolvedBrioTeam
          : fuzzyMatchMultiple(sampleRow?.brioTeamRaw || '', userOptions),
        budget: rowBudget,
        lastEngaged: sampleRow?.lastEngaged || '',
        rating: sampleRow?.rating != null ? sampleRow.rating : undefined,
        notes: sampleRow?.companyNotes || sampleRow?.notes || '',
        paymentTerms: sampleRow?.paymentTerms || '',
        location: sampleRow?.location || '',
        street: sampleRow?.street || '',
        city: sampleRow?.city || '',
        state: sampleRow?.state || '',
        country: sampleRow?.country || '',
        postcode: sampleRow?.postcode || '',
      });
      if (result?.success && result.id) {
        newCompanyIdMap[name.toLowerCase()] = result.id;
      }
    }

    // 2. Create / overwrite / skip contact people for each row
    for (const row of rows) {
      // Overwrite: update the existing contact record using per-field selections
      if (row._duplicateContact && row._duplicateAction === 'overwrite') {
        const existing = row._duplicateContact;
        const sels = row._fieldSelections || {};
        const edits = row._fieldEdits || {};
        const resolveField = (key, existingVal, incomingVal, mergeable = false) => {
          const ev = edits[key]?.existing !== undefined ? edits[key].existing : (existingVal || '');
          const iv = edits[key]?.incoming !== undefined ? edits[key].incoming : (incomingVal || '');
          const sel = sels[key] || defaultFieldSel(ev, iv, mergeable);
          if (sel === 'existing') return ev;
          if (sel === 'incoming') return iv;
          return combineText(ev, iv); // 'both' → merge
        };
        await updateVendorContact(existing.id, {
          name: row.name || existing.name || '',
          designation: resolveField('designation', existing.designation, row.designation),
          phone: resolveField('phone', existing.phone, row.phone),
          altPhone: resolveField('altPhone', existing.altPhone, row.altPhone),
          fax: resolveField('fax', existing.fax, row.fax),
          email: resolveField('email', existing.email, row.email),
          linkedin: cleanLinkedinUrl(resolveField('linkedin', existing.linkedin, row.linkedin)),
          instagram: resolveField('instagram', existing.instagram, row.instagram),
          notes: resolveField('notes', existing.notes, row.contactNotes || row.notes, true),
        });
        // Also update the linked vendor's workedWithBefore and notes (combine)
        const linkedVendor = (vendors || []).find(v => v.id === existing.vendorId);
        if (linkedVendor && updateVendor) {
          const vendorUpdates = {};
          if (row.workedWithBefore)
            vendorUpdates.workedWithBefore = combineText(linkedVendor.workedWithBefore, row.workedWithBefore);
          const incomingVendorNotes = row.companyNotes || row.notes;
          if (incomingVendorNotes)
            vendorUpdates.notes = combineText(linkedVendor.notes, incomingVendorNotes);
          if (row.paymentTerms)
            vendorUpdates.paymentTerms = combineText(linkedVendor.paymentTerms, row.paymentTerms);
          if (Object.keys(vendorUpdates).length > 0)
            await updateVendor(linkedVendor.id, vendorUpdates);
        }
        continue;
      }

      // No duplicate, or user chose 'add': create a new contact
      let vendorId = null;
      if (row._selectedVendorId === '__new__') {
        vendorId = newCompanyIdMap[(row._newCompanyName || '').toLowerCase()] || null;
      } else {
        vendorId = Number(row._selectedVendorId);
      }

      if (!vendorId) continue;

      await createVendorContact({
        vendorId,
        name: row.name || '',
        designation: row.designation || '',
        phone: row.phone || '',
        altPhone: row.altPhone || '',
        fax: row.fax || '',
        email: row.email || '',
        linkedin: row.linkedin || '',
        instagram: row.instagram || '',
        notes: row.contactNotes || row.notes || '',
      });
    }
  }, [createVendor, updateVendor, createVendorContact, updateVendorContact, projectOptions, userOptions, vendors]);

  const handleMergeVendors = useCallback(async (vendorA, vendorB, mergedFields) => {
    if (!updateVendor || !deleteVendor) return { success: false };
    // 1. Write merged fields onto the winner (A)
    const result = await updateVendor(vendorA.id, mergedFields);
    if (!result?.success) return result;
    // 2. Re-home all of B's contacts to A
    const bContacts = (vendorContacts || []).filter(c => c.vendorId === vendorB.id);
    for (const contact of bContacts) {
      await updateVendorContact(contact.id, { vendorId: vendorA.id });
    }
    // 3. Delete the loser
    return deleteVendor(vendorB.id);
  }, [updateVendor, updateVendorContact, deleteVendor, vendorContacts]);

  const handleMergeContacts = useCallback(async (contactA, contactB, mergedFields) => {
    if (!updateVendorContact || !deleteVendorContact) return { success: false };
    // 1. Write merged fields onto the winner (A)
    const result = await updateVendorContact(contactA.id, mergedFields);
    if (!result?.success) return result;
    // 2. Delete the loser
    return deleteVendorContact(contactB.id);
  }, [updateVendorContact, deleteVendorContact]);

  // ── Enrichment ─────────────────────────────────────────────────────────
  const handleEnrichVendor = useCallback(async (vendor) => {
    setEnrichmentModal({
      vendorId: vendor.id,
      vendorName: vendor.companyName,
      currentData: vendor,
      loading: true,
      error: null,
      results: null,
      source: null,
    });
    try {
      // Gather contact people linked to this company for richer search context
      const contacts = (vendorContacts || [])
        .filter(c => c.vendorId === vendor.id)
        .map(c => ({ name: c.name, designation: c.designation, email: c.email }));

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';
      const response = await fetch('/api/company-enrichment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          companyName: vendor.companyName,
          location: vendor.location || [vendor.city, vendor.country].filter(Boolean).join(' '),
          contacts,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Server error (${response.status})`);
      }
      const data = await response.json();
      setEnrichmentModal(prev => ({
        ...prev,
        loading: false,
        results: data.results || [],
        source: data.source,
        googleKeyMissing: data.googleKeyMissing || false,
      }));
    } catch (err) {
      setEnrichmentModal(prev => ({ ...prev, loading: false, error: err.message }));
    }
  }, [vendorContacts]);

  const handleApplyEnrichment = useCallback(async (vendorId, updates) => {
    if (!updateVendor) return { success: false, error: 'Update not available' };
    // Clean website URL before saving
    if (updates.website) updates.website = cleanWebsite(updates.website);
    const result = await updateVendor(vendorId, updates);
    if (result?.success) {
      setEnrichmentModal(null);
    }
    return result;
  }, [updateVendor]);

  // ── Sort toggle ────────────────────────────────────────────────────────
  const toggleSort = (key) => {
    setSortRules(prev => {
      const existing = prev.find(r => r.key === key);
      if (existing) {
        return prev.map(r => r.key === key ? { ...r, dir: r.dir === 'asc' ? 'desc' : 'asc' } : r);
      }
      return [{ key, dir: 'asc' }];
    });
  };

  // Unique specialism values from all vendors for auto-suggest

  // Unique designation values from all contacts for auto-suggest
  const designationSuggestions = useMemo(() => {
    const set = new Set();
    (vendorContacts || []).forEach(c => { if (c.designation) set.add(c.designation); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [vendorContacts]);

  // Unique values per filterable column (for filter panel dropdowns)
  const uniqueValues = useMemo(() => {
    const result = {};
    for (const col of FILTERABLE_COLUMNS) {
      const set = new Set();
      (vendors || []).forEach(v => { const val = (v[col.key] || '').trim(); if (val) set.add(val); });
      result[col.key] = [...set].sort((a, b) => a.localeCompare(b));
    }
    return result;
  }, [vendors]);

  // ── Cell rendering ─────────────────────────────────────────────────────
  const CELL_STYLE = {
    padding: '4px 8px',
    fontSize: '11px',
    borderBottom: '1px solid ' + BRIO_BORDER,
    overflow: 'visible',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
    fontFamily: FONT,
    position: 'relative',
  };

  const renderCell = (col, vendor) => {
    switch (col.key) {
      case 'companyName': {
        const contactCount = (vendorContacts || []).filter(c => c.vendorId === vendor.id).length;
        return (
          <td key={col.key} style={{ ...CELL_STYLE, fontWeight: 600, color: BRIO_GOLD, minWidth: col.minW, maxWidth: col.flexW, overflow: 'hidden' }}>
            <span
              onClick={() => setExpandedId(expandedId === vendor.id ? null : vendor.id)}
              onDoubleClick={e => { e.stopPropagation(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                maxWidth: '100%', overflow: 'hidden',
              }}
            >
              {expandedId === vendor.id ? <ChevronDown size={12} style={{ flexShrink: 0 }} /> : <ChevronRight size={12} style={{ flexShrink: 0 }} />}
              <CompanyNameCell
                value={vendor.companyName}
                onChange={v => handleFieldSave(vendor.id, 'companyName', v)}
              />
              {contactCount > 0 && (
                <span style={{ fontSize: '9px', color: '#888', fontWeight: 400, flexShrink: 0 }}>
                  ({contactCount})
                </span>
              )}
            </span>
          </td>
        );
      }
      case 'companyEmail':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <EditableCell value={vendor.companyEmail} onChange={v => handleFieldSave(vendor.id, 'companyEmail', v)} placeholder="email@company.com" />
          </td>
        );
      case 'website':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <EditableCell value={vendor.website} onChange={v => handleFieldSave(vendor.id, 'website', cleanWebsite(v))} placeholder="example.com" style={{ flex: 1 }} />
              {vendor.website && (
                <a href={vendor.website.startsWith('http') ? vendor.website : 'https://' + vendor.website}
                  target="_blank" rel="noopener noreferrer" style={{ color: BRIO_GOLD, flexShrink: 0 }}>
                  <ExternalLink size={11} />
                </a>
              )}
            </div>
          </td>
        );
      case 'instagram':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <EditableCell value={vendor.instagram} onChange={v => handleFieldSave(vendor.id, 'instagram', v)} placeholder="@handle" style={{ flex: 1 }} />
              {vendor.instagram && (
                <a href={vendor.instagram.startsWith('http') ? vendor.instagram : 'https://instagram.com/' + vendor.instagram.replace(/^@/, '')}
                  target="_blank" rel="noopener noreferrer" style={{ color: BRIO_GOLD, flexShrink: 0 }}>
                  <Instagram size={11} />
                </a>
              )}
            </div>
          </td>
        );
      case 'dropboxUrl':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <EditableCell value={vendor.dropboxUrl} onChange={v => handleFieldSave(vendor.id, 'dropboxUrl', v)} placeholder="Dropbox link" style={{ flex: 1 }} />
              {vendor.dropboxUrl && (
                <a href={vendor.dropboxUrl.startsWith('http') ? vendor.dropboxUrl : 'https://' + vendor.dropboxUrl}
                  target="_blank" rel="noopener noreferrer" style={{ color: BRIO_GOLD, flexShrink: 0 }}>
                  <FolderOpen size={11} />
                </a>
              )}
            </div>
          </td>
        );
      case 'budget':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <EditableCell
              value={vendor.budget}
              onChange={v => handleFieldSave(vendor.id, 'budget', v)}
              type="select"
              options={['High', 'Mid', 'Low']}
              placeholder="—"
            />
          </td>
        );
      case 'category':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <EditableCell
              value={vendor.category} type="select" options={visibleCategoryOptions}
              onChange={v => handleFieldSave(vendor.id, 'category', v)}
              displayValue={vendor.category ? (
                <span style={{
                  fontSize: '10px', padding: '1px 6px', borderRadius: 3, fontWeight: 500,
                  background: (CATEGORY_BADGE_COLORS[vendor.category] || CATEGORY_BADGE_COLORS['Vendor / Supplier']).bg,
                  color: (CATEGORY_BADGE_COLORS[vendor.category] || CATEGORY_BADGE_COLORS['Vendor / Supplier']).color,
                }}>{vendor.category}</span>
              ) : undefined}
            />
          </td>
        );
      case 'specialism':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <SpecialismSelect
              value={vendor.specialism}
              options={specialismHook.getOptionsForCategory(vendor.category)}
              onChange={v => handleFieldSave(vendor.id, 'specialism', v)}
              onAddOption={specialismHook.addOption}
              vendorCategory={vendor.category}
              placeholder="Select specialism"
            />
          </td>
        );
      case 'workedWithBefore':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <EditableCell value={vendor.workedWithBefore} onChange={v => handleFieldSave(vendor.id, 'workedWithBefore', v)} placeholder="—" />
          </td>
        );
      case 'brioProject':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <MultiSelectDropdown
              value={vendor.brioProject}
              options={projectOptions}
              onChange={v => handleFieldSave(vendor.id, 'brioProject', v)}
              placeholder="Select projects"
            />
          </td>
        );
      case 'brioTeamInvolved':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <MultiSelectDropdown
              value={vendor.brioTeamInvolved}
              options={userOptions}
              onChange={v => handleFieldSave(vendor.id, 'brioTeamInvolved', v)}
              placeholder="Select team"
            />
          </td>
        );
      case 'rating':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW, textAlign: 'center' }}>
            <StarRating value={vendor.rating} onChange={v => handleFieldSave(vendor.id, 'rating', v)} />
          </td>
        );
      case 'location':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <LocationBadge vendor={vendor} onSave={fields => handleLocationSave(vendor.id, fields)} />
          </td>
        );
      case 'phone':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <EditableCell value={vendor.phone} onChange={v => handleFieldSave(vendor.id, 'phone', v)} placeholder="+91..." />
          </td>
        );
      case 'lastEngaged':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <EditableCell value={vendor.lastEngaged} type="date" onChange={v => handleFieldSave(vendor.id, 'lastEngaged', v)} />
          </td>
        );
      case 'paymentTerms':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <EditableCell value={vendor.paymentTerms} onChange={v => handleFieldSave(vendor.id, 'paymentTerms', v)} placeholder="e.g. Net 30" />
          </td>
        );
      case 'notes':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW, maxWidth: col.flexW }}>
            <span
              onClick={() => setNotesDrawer({ vendorId: vendor.id, name: vendor.companyName, notes: vendor.notes })}
              style={{
                cursor: 'pointer', display: 'block', minHeight: '16px',
                borderBottom: readOnly ? 'none' : '1px dashed ' + BRIO_GOLD + '60',
                color: vendor.notes ? '#1e293b' : '#999',
                fontSize: '11px', lineHeight: '16px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
              title={vendor.notes || (readOnly ? 'View notes' : 'Click to edit')}
            >
              {vendor.notes || (readOnly ? '—' : 'Add notes...')}
            </span>
          </td>
        );
      case 'dateAdded':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
            <span style={{ fontSize: '10px', color: '#888' }}>{vendor.dateAdded || '—'}</span>
          </td>
        );
      case 'commsEnabled':
        return (
          <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW, textAlign: 'center' }}>
            <input
              type="checkbox"
              checked={!!vendor.commsEnabled}
              disabled={readOnly}
              onChange={e => handleFieldSave(vendor.id, 'commsEnabled', e.target.checked)}
              title="Include in Communications contacts"
              style={{ cursor: readOnly ? 'default' : 'pointer', accentColor: BRIO_GOLD }}
            />
          </td>
        );
      default:
        return <td key={col.key} style={CELL_STYLE}>—</td>;
    }
  };

  // Measure the container's top offset to calculate available height for sticky behavior.
  // The component lives inside App.js's content area (below fixed nav + padding).
  // We measure once after mount to get the viewport-relative top position.
  const containerRef = useRef(null);
  const [containerTop, setContainerTop] = useState(0);
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerTop(containerRef.current.getBoundingClientRect().top);
      }
    };
    measure();
    const timer = setTimeout(measure, 200);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(timer); window.removeEventListener('resize', measure); };
  }, []);

  // ── Screenshot / capture deterrent (read-only viewers only) ──────────────
  // Best-effort only: no browser can truly prevent a screenshot (a phone camera
  // always can). These measures raise the bar for casual capture of a read-only
  // contacts view — printing / print-to-PDF is blanked, and the content is
  // masked whenever the tab is hidden, the window loses focus, or PrintScreen is
  // pressed (which also clears whatever it may have copied). Combined with the
  // page's existing copy / context-menu blocks and inert inputs.
  const [captureShield, setCaptureShield] = useState(false);
  useEffect(() => {
    if (!readOnly) return undefined;

    // 1. Blank the whole page for print / print-to-PDF.
    const style = document.createElement('style');
    style.setAttribute('data-contacts-print-guard', '');
    style.textContent = '@media print { body { display: none !important; } }';
    document.head.appendChild(style);

    // 2. Mask on focus loss / tab switch — many capture tools defocus the page.
    const shield = () => setCaptureShield(true);
    const unshield = () => setCaptureShield(false);
    const onVisibility = () => { if (document.hidden) setCaptureShield(true); };
    const onKey = (e) => {
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
        setCaptureShield(true);
        try { if (navigator.clipboard) navigator.clipboard.writeText(''); } catch (_) { /* best effort */ }
        setTimeout(() => setCaptureShield(false), 1200);
      }
    };
    window.addEventListener('blur', shield);
    window.addEventListener('focus', unshield);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('keyup', onKey);
    window.addEventListener('keydown', onKey);

    return () => {
      if (style.parentNode) style.parentNode.removeChild(style);
      window.removeEventListener('blur', shield);
      window.removeEventListener('focus', unshield);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('keydown', onKey);
    };
  }, [readOnly]);

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════

  return (
    <ContactsReadOnlyContext.Provider value={readOnly}>
    <div ref={containerRef} style={{
      fontFamily: FONT,
      display: 'flex', flexDirection: 'column',
      height: containerTop > 0 ? `calc(100vh - ${containerTop}px - 16px)` : 'calc(100vh - 140px)',
      ...(readOnly ? { userSelect: 'none', WebkitUserSelect: 'none', position: 'relative' } : null),
    }}>

      {/* Capture-deterrent overlay for read-only viewers (see effect above). */}
      {readOnly && captureShield && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 12000,
          background: '#111', color: '#fff', display: 'flex',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          fontFamily: FONT, fontSize: '13px', fontWeight: 400, padding: 24,
        }}>
          <div>
            <Lock size={20} style={{ marginBottom: 8, opacity: 0.85 }} />
            <div>This page is protected. Screenshots and printing are disabled.</div>
          </div>
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
        padding: '8px 4px', flexShrink: 0,
        background: '#fff', borderBottom: '1px solid ' + BRIO_BORDER,
      }}>
        {/* Left: action buttons (hidden for read-only viewers) */}
        {!readOnly && (
          <>
            <button
              onClick={() => setShowImportModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', fontSize: '11px', fontWeight: 500, fontFamily: FONT,
                background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer',
                color: '#555',
              }}
            >
              <Upload size={12} /> Import
            </button>
            <button
              onClick={() => setShowMergeModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', fontSize: '11px', fontWeight: 500, fontFamily: FONT,
                background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, cursor: 'pointer',
                color: '#555',
              }}
            >
              <GitMerge size={12} /> Find Duplicates
            </button>
            <button
              onClick={() => setAddingRow(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', fontSize: '11px', fontWeight: 500, fontFamily: FONT,
                background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
              }}
            >
              <Plus size={12} /> Add Company
            </button>
          </>
        )}

        <div style={{ flex: 1 }} />

        {/* Right: count, sort, filter, search */}
        <span style={{ fontSize: '10px', color: '#888' }}>
          {filteredVendors.length} compan{filteredVendors.length === 1 ? 'y' : 'ies'}
        </span>

        {/* Sort */}
        <div style={{ position: 'relative' }}>
          <ToolbarIcon
            onClick={() => { setShowSortPanel(!showSortPanel); setShowFilterPanel(false); }}
            tooltip="Sort"
            active={sortRules.length > 0}
            badge={sortRules.length || null}
          >
            <ArrowUpDown size={13} />
          </ToolbarIcon>
          {showSortPanel && (
            <SortPanel sortRules={sortRules} onChangeSortRules={setSortRules} onClose={() => setShowSortPanel(false)} />
          )}
        </div>

        {/* Filter */}
        <div style={{ position: 'relative' }}>
          <ToolbarIcon
            onClick={() => { setShowFilterPanel(!showFilterPanel); setShowSortPanel(false); }}
            tooltip="Filter"
            active={filterRules.length > 0}
            badge={filterRules.length || null}
          >
            <Filter size={13} />
          </ToolbarIcon>
          {showFilterPanel && (
            <FilterPanel filterRules={filterRules} onChangeFilterRules={setFilterRules} onClose={() => setShowFilterPanel(false)} uniqueValues={uniqueValues} />
          )}
        </div>

        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 2, alignItems: 'center', borderLeft: '1px solid ' + BRIO_BORDER, paddingLeft: 8, marginLeft: 2 }}>
          {visibleCategoryOptions.map(cat => {
            const isActive = activeCategoryTab === cat;
            const badge = CATEGORY_BADGE_COLORS[cat];
            return (
              <button
                key={cat}
                onClick={() => setActiveCategoryTab(isActive ? null : cat)}
                style={{
                  padding: '3px 8px', fontSize: '10px', fontWeight: 600,
                  fontFamily: FONT, borderRadius: 3, cursor: 'pointer',
                  border: '1px solid ' + (isActive ? (badge?.color || BRIO_GOLD) : 'transparent'),
                  background: isActive ? (badge?.bg || BRIO_GOLD + '15') : 'transparent',
                  color: isActive ? (badge?.color || BRIO_GOLD) : '#888',
                  transition: 'all 0.15s ease', lineHeight: '16px',
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <Search size={12} style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search…"
            style={{
              fontSize: '11px', padding: '4px 6px 4px 22px', width: 140,
              border: '1px solid ' + BRIO_BORDER, borderRadius: 4, fontFamily: FONT,
              outline: 'none',
            }}
          />
        </div>

        {/* Give access — admin only. Grants Read only / Full access to the page. */}
        {canManageAccess && (
          <ToolbarIcon
            onClick={() => setShowGiveAccessModal(true)}
            tooltip="Give access"
          >
            <Users size={13} />
          </ToolbarIcon>
        )}
      </div>

      {/* Active filter chips */}
      {filterRules.length > 0 && (
        <div style={{ padding: '6px 4px', display: 'flex', gap: 6, flexWrap: 'wrap', background: '#fefef8', borderBottom: '1px solid ' + BRIO_BORDER, flexShrink: 0 }}>
          {filterRules.map((rule, idx) => {
            const col = FILTERABLE_COLUMNS.find(c => c.key === rule.key);
            return (
              <span key={idx} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: '10px', padding: '2px 8px', background: BRIO_GOLD + '15',
                border: '1px solid ' + BRIO_GOLD + '30', borderRadius: 3,
                fontFamily: FONT, color: BRIO_GOLD, fontWeight: 500,
              }}>
                {col?.label} {rule.condition} "{rule.value}"
                <button
                  onClick={() => setFilterRules(filterRules.filter((_, i) => i !== idx))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_GOLD, padding: 0 }}
                ><X size={10} /></button>
              </span>
            );
          })}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────── */}
      <div
        style={{ overflowX: 'auto', overflowY: 'auto', flex: 1 }}
        onCopy={e => {
          // Allow copying within a single row; block multi-row copies
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;
          const range = sel.getRangeAt(0);
          const startRow = range.startContainer?.nodeType === 1
            ? range.startContainer.closest?.('tr')
            : range.startContainer?.parentElement?.closest?.('tr');
          const endRow = range.endContainer?.nodeType === 1
            ? range.endContainer.closest?.('tr')
            : range.endContainer?.parentElement?.closest?.('tr');
          if (!startRow || !endRow || startRow !== endRow) e.preventDefault();
        }}
        onContextMenu={e => e.preventDefault()}
      >
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', border: '1px solid ' + BRIO_BORDER, borderRadius: 6, background: '#fff' }}>
          <colgroup>
            {orderedColumns.map(col => {
              const isCollapsed = collapsedCols[col.key];
              const w = isCollapsed ? COLLAPSED_W : col.flexW;
              return <col key={col.key} style={{ width: w + 'px', minWidth: (isCollapsed ? COLLAPSED_W : col.minW) + 'px' }} />;
            })}
            <col style={{ width: 36 }} />
          </colgroup>
          <thead>
            <tr>
              {orderedColumns.map((col, colIdx) => {
                const isCollapsed = collapsedCols[col.key];
                const isFrozen = col.alwaysFrozen || frozenCols[col.key];
                const frozenLeft = isFrozen ? computeFrozenLeft(orderedColumns, collapsedCols, frozenCols, colIdx) : undefined;
                const isDragOver = dragOverColIdx === colIdx && dragColIdxRef.current !== colIdx;

                const stickyStyle = isFrozen ? {
                  position: 'sticky', left: frozenLeft + 'px', zIndex: 15,
                  borderRight: '2px solid ' + BRIO_GOLD + '40',
                } : {};

                const HEADER_STYLE = {
                  padding: '6px 5px', fontSize: '9px', fontWeight: 600,
                  textAlign: 'left', borderBottom: '2px solid ' + BRIO_GOLD,
                  color: '#555', userSelect: 'none', whiteSpace: 'nowrap', fontFamily: FONT,
                  background: BRIO_BG, zIndex: isFrozen ? 14 : 10,
                  position: 'sticky', top: 0,
                };

                if (isCollapsed) {
                  return (
                    <th key={col.key} style={{ ...HEADER_STYLE, ...stickyStyle, cursor: 'pointer', padding: '4px 2px', textAlign: 'center' }}
                      onClick={() => toggleColCollapse(col.key)} title={'Expand ' + col.label}
                    >
                      <ChevronRight size={10} style={{ color: BRIO_GREY }} />
                    </th>
                  );
                }
                return (
                  <th key={col.key}
                    draggable={!col.alwaysFrozen && !frozenCols[col.key]}
                    onDragStart={e => {
                      if (col.alwaysFrozen || frozenCols[col.key]) { e.preventDefault(); return; }
                      dragColIdxRef.current = colIdx;
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(colIdx));
                    }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverColIdx(colIdx); }}
                    onDragLeave={() => setDragOverColIdx(null)}
                    onDrop={e => {
                      e.preventDefault();
                      const fromIdx = dragColIdxRef.current;
                      if (fromIdx != null && fromIdx !== colIdx) moveColumn(fromIdx, colIdx);
                      dragColIdxRef.current = null; setDragOverColIdx(null);
                    }}
                    onDragEnd={() => { dragColIdxRef.current = null; setDragOverColIdx(null); }}
                    style={{
                      ...HEADER_STYLE, ...stickyStyle,
                      cursor: 'default',
                      borderLeft: isDragOver ? '2px solid ' + BRIO_GOLD : undefined,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'space-between' }}>
                      {!col.alwaysFrozen && (
                        <GripVertical size={12} style={{ color: BRIO_GREY, cursor: 'grab', flexShrink: 0, opacity: 0.5 }} />
                      )}
                      <span
                        onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                        style={{ cursor: col.sortable ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: '1px', flex: 1, overflow: 'hidden' }}
                      >
                        {col.label}
                        {col.sortable && sortRules.some(r => r.key === col.key) && (() => {
                          const rule = sortRules.find(r => r.key === col.key);
                          return (
                            <span style={{ marginLeft: 2, flexShrink: 0 }}>
                              {rule.dir === 'asc' ? <ChevronDown size={9} style={{ color: BRIO_GOLD }} /> : <ChevronDown size={9} style={{ color: BRIO_GOLD, transform: 'rotate(180deg)' }} />}
                            </span>
                          );
                        })()}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0px', flexShrink: 0 }}>
                        {!col.alwaysFrozen && (
                          <span
                            onClick={e => { e.stopPropagation(); toggleFreezeCol(col.key); }}
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: frozenCols[col.key] ? BRIO_GOLD : BRIO_GREY, opacity: frozenCols[col.key] ? 1 : 0.4 }}
                            title={frozenCols[col.key] ? 'Unfreeze column' : 'Freeze column'}
                          >
                            {frozenCols[col.key] ? <Lock size={8} /> : <Unlock size={8} />}
                          </span>
                        )}
                        {!col.alwaysFrozen && (
                          <span
                            onClick={e => { e.stopPropagation(); toggleColCollapse(col.key); }}
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: BRIO_GREY, flexShrink: 0 }}
                            title="Collapse column"
                          >
                            <ChevronLeft size={9} />
                          </span>
                        )}
                      </span>
                    </span>
                  </th>
                );
              })}
              {/* Enrichment column (non-sticky) */}
              <th style={{
                padding: '6px 4px', fontSize: '9px', fontWeight: 600,
                borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG,
                width: 28,
              }} />
              {/* Delete column (sticky right) */}
              <th style={{
                padding: '6px 4px', fontSize: '9px', fontWeight: 600,
                borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG,
                position: 'sticky', right: 0, zIndex: 11, width: 28,
                boxShadow: '-2px 0 4px rgba(0,0,0,0.06)',
              }} />
            </tr>
          </thead>
          <tbody>
            {/* Loading */}
            {loading && (
              <tr><td colSpan={orderedColumns.length + 2} style={{ ...CELL_STYLE, textAlign: 'center', color: '#aaa', padding: 24 }}>Loading...</td></tr>
            )}

            {/* Fetch error — surfaces silent RLS/network failures so granted
                users don't see an unexplained empty table. */}
            {!loading && vendorsError && (
              <tr><td colSpan={orderedColumns.length + 2} style={{ ...CELL_STYLE, textAlign: 'center', color: '#b91c1c', padding: 24 }}>
                Could not load contacts: {vendorsError}
              </td></tr>
            )}

            {/* Empty state */}
            {!loading && !vendorsError && filteredVendors.length === 0 && !addingRow && (
              <tr><td colSpan={orderedColumns.length + 2} style={{ ...CELL_STYLE, textAlign: 'center', color: '#aaa', padding: 24 }}>
                No companies found. Click &ldquo;Add Company&rdquo; to get started.
              </td></tr>
            )}

            {/* Adding new row */}
            {addingRow && (
              <tr style={{ background: 'var(--brio-surface-accent, #f5f3e8)' }}>
                {orderedColumns.map((col, idx) => {
                  if (col.key === 'companyName') {
                    return (
                      <td key={col.key} style={{ ...CELL_STYLE, minWidth: col.minW }}>
                        <CompanyNameAutocomplete
                          value={newRow.companyName || ''}
                          onChange={v => setNewRow(r => ({ ...r, companyName: v }))}
                          vendors={vendors}
                          onSelectExisting={vendor => {
                            setAddingRow(false);
                            setNewRow({});
                            setExpandedId(vendor.id);
                            setHighlightedVendorId(vendor.id);
                            setTimeout(() => setHighlightedVendorId(null), 2000);
                            setTimeout(() => {
                              const el = document.querySelector(`[data-vendor-id="${vendor.id}"]`);
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                          }}
                          placeholder="Company name *"
                          autoFocus
                          style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: 2, fontFamily: FONT, width: '100%', fontWeight: 600 }}
                        />
                      </td>
                    );
                  }
                  if (col.key === 'companyEmail') return <td key={col.key} style={CELL_STYLE}><input type="text" value={newRow.companyEmail || ''} onChange={e => setNewRow(r => ({ ...r, companyEmail: e.target.value }))} placeholder="email" style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, width: '100%' }} /></td>;
                  if (col.key === 'website') return <td key={col.key} style={CELL_STYLE}><input type="text" value={newRow.website || ''} onChange={e => setNewRow(r => ({ ...r, website: e.target.value }))} placeholder="website" style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, width: '100%' }} /></td>;
                  if (col.key === 'instagram') return <td key={col.key} style={CELL_STYLE}><input type="text" value={newRow.instagram || ''} onChange={e => setNewRow(r => ({ ...r, instagram: e.target.value }))} placeholder="@handle" style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, width: '100%' }} /></td>;
                  if (col.key === 'category') return <td key={col.key} style={CELL_STYLE}><select value={newRow.category || ''} onChange={e => setNewRow(r => ({ ...r, category: e.target.value }))} style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, width: '100%' }}><option value="">—</option>{visibleCategoryOptions.map(c => <option key={c} value={c}>{c}</option>)}</select></td>;
                  if (col.key === 'specialism') return <td key={col.key} style={CELL_STYLE}><input type="text" value={newRow.specialism || ''} onChange={e => setNewRow(r => ({ ...r, specialism: e.target.value }))} placeholder="specialism" style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_BORDER, borderRadius: 2, fontFamily: FONT, width: '100%' }} /></td>;
                  if (collapsedCols[col.key]) return <td key={col.key} style={{ ...CELL_STYLE, padding: '4px 0' }} />;
                  return <td key={col.key} style={CELL_STYLE}><span style={{ color: '#ccc', fontSize: '10px' }}>—</span></td>;
                })}
                <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={handleAddCompany} disabled={!newRow.companyName?.trim()}
                      style={{ fontSize: '10px', padding: '2px 8px', background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontFamily: FONT, opacity: newRow.companyName?.trim() ? 1 : 0.5 }}>
                      Save
                    </button>
                    <button onClick={() => { setAddingRow(false); setNewRow({}); }}
                      style={{ fontSize: '10px', padding: '2px 6px', background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, cursor: 'pointer', fontFamily: FONT }}>
                      <X size={10} />
                    </button>
                  </div>
                </td>
                {/* sticky delete column placeholder */}
                <td style={{ ...CELL_STYLE, position: 'sticky', right: 0, background: 'var(--brio-surface-accent, #f5f3e8)', boxShadow: '-2px 0 4px rgba(0,0,0,0.06)' }} />
              </tr>
            )}

            {/* Data rows */}
            {filteredVendors.map(vendor => (
              <React.Fragment key={vendor.id}>
                <tr
                  data-vendor-id={vendor.id}
                  style={vendor.id === highlightedVendorId ? { background: 'rgba(159,144,42,0.15)', transition: 'background 0.5s' } : undefined}
                  onMouseEnter={e => { if (vendor.id !== highlightedVendorId) e.currentTarget.style.background = '#fafaf4'; }}
                  onMouseLeave={e => { if (vendor.id !== highlightedVendorId) e.currentTarget.style.background = ''; }}
                >
                  {orderedColumns.map((col, colIdx) => {
                    const isFrozen = col.alwaysFrozen || frozenCols[col.key];
                    const frozenLeft = isFrozen ? computeFrozenLeft(orderedColumns, collapsedCols, frozenCols, colIdx) : undefined;
                    const frozenTdStyle = isFrozen ? {
                      position: 'sticky', left: frozenLeft + 'px', zIndex: 5,
                      background: '#fff', borderRight: '2px solid ' + BRIO_GOLD + '40',
                    } : {};

                    if (collapsedCols[col.key]) {
                      return <td key={col.key} style={{ ...CELL_STYLE, ...frozenTdStyle, padding: '4px 0' }} />;
                    }
                    const cell = renderCell(col, vendor);
                    if (!isFrozen) return cell;
                    // Wrap frozen cell with sticky style
                    return React.cloneElement(cell, {
                      style: { ...cell.props.style, ...frozenTdStyle },
                    });
                  })}
                  {/* Enrichment button — non-sticky */}
                  <td style={{ ...CELL_STYLE, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {!readOnly && (isAdmin || currentUser?.role === 'supervisor') && (() => {
                      const recentlyEnriched = vendor.enrichmentCheckedAt &&
                        (Date.now() - new Date(vendor.enrichmentCheckedAt).getTime()) < 24 * 60 * 60 * 1000;
                      return (
                        <button
                          onClick={() => {
                            if (recentlyEnriched) {
                              const hrs = Math.round((Date.now() - new Date(vendor.enrichmentCheckedAt).getTime()) / 3600000);
                              if (!window.confirm(`Last enriched ${hrs}h ago. Search again?`)) return;
                            }
                            handleEnrichVendor(vendor);
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: BRIO_GOLD, opacity: recentlyEnriched ? 0.4 : 1 }}
                          title={recentlyEnriched ? `Last enriched ${Math.round((Date.now() - new Date(vendor.enrichmentCheckedAt).getTime()) / 3600000)}h ago` : 'Auto-fill company data'}
                        >
                          <Wand2 size={12} />
                        </button>
                      );
                    })()}
                  </td>
                  {/* Delete button — sticky right */}
                  <td style={{ ...CELL_STYLE, textAlign: 'center', position: 'sticky', right: 0, zIndex: 6, background: '#fff', boxShadow: '-2px 0 4px rgba(0,0,0,0.06)' }}>
                    {!readOnly && (
                      <button
                        onClick={() => handleDeleteCompany(vendor.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: BRIO_RED }}
                        title="Delete company"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
                {/* Inline contacts drawer below company row */}
                {expandedId === vendor.id && (
                  <InlineContactsRow
                    colSpan={orderedColumns.length + 2}
                    contacts={(vendorContacts || []).filter(c => c.vendorId === vendor.id)}
                    vendors={vendors}
                    currentVendorId={vendor.id}
                    onAddContact={handleAddContact}
                    onUpdateContact={handleUpdateContact}
                    onDeleteContact={handleDeleteContact}
                    onMoveContact={handleMoveContact}
                    onCreateAndMoveContact={handleCreateAndMoveContact}
                    designationSuggestions={designationSuggestions}
                  />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Notes Drawer ─────────────────────────────────────────────── */}
      {notesDrawer && (
        <NotesDrawer
          vendorName={notesDrawer.name}
          value={notesDrawer.notes}
          onChange={v => handleFieldSave(notesDrawer.vendorId, 'notes', v)}
          onClose={() => setNotesDrawer(null)}
        />
      )}

      {/* ── Import Modal ────────────────────────────────────────────── */}
      {showImportModal && (
        <ImportContactsModal
          vendors={vendors}
          vendorContacts={vendorContacts}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportContacts}
          specialismOptions={specialismHook.allOptions}
          categoryOptions={visibleCategoryOptions}
          projectOptions={projectOptions}
          userOptions={userOptions}
        />
      )}

      {/* ── Merge Duplicates Modal ───────────────────────────────────── */}
      {showMergeModal && (
        <MergeContactsModal
          vendors={vendors}
          vendorContacts={vendorContacts}
          onClose={() => setShowMergeModal(false)}
          onMergeVendors={handleMergeVendors}
          onMergeContacts={handleMergeContacts}
        />
      )}
      {enrichmentModal && (
        <EnrichmentReviewModal
          modal={enrichmentModal}
          onClose={() => setEnrichmentModal(null)}
          onApply={handleApplyEnrichment}
        />
      )}

      {/* ── Give Access Modal — lists active users only ──────────────── */}
      {showGiveAccessModal && (
        <ContactsGiveAccessModal
          users={activeUsers.filter(u => u.isActive !== false && u.status !== 'inactive')}
          currentAccess={contactsAccess}
          onSave={async (map) => {
            if (!onSaveContactsAccess) { setShowGiveAccessModal(false); return { success: true }; }
            const res = await onSaveContactsAccess(map);
            // Keep the modal open (with the error) when the write was rejected,
            // so the selection is not lost and the reason is visible.
            if (res && res.success === false) return res;
            setShowGiveAccessModal(false);
            return { success: true };
          }}
          onClose={() => setShowGiveAccessModal(false)}
        />
      )}
    </div>
    </ContactsReadOnlyContext.Provider>
  );
}
